<template>
  <div>
    <CrmPageHeader
      :title="oportunidade ? oportunidade.titulo : 'Oportunidade'"
      :back-label="backLabel"
      :back-to="backTo"
      :subtitle="oportunidadeSubtitle"
    ></CrmPageHeader>

    <CrmEmptyState
      v-if="loading"
      title="Carregando oportunidade..."
      description="Buscando dados comerciais, contato e histórico."
      icon="la la-refresh"
    />

    <div v-else-if="oportunidade" class="crm-opportunity">
      <section class="crm-opportunity-hero">
        <div>
          <span class="crm-eyebrow">Oportunidade</span>
          <h2>{{ oportunidade.titulo }}</h2>
          <div class="crm-hero-meta">
            <span>{{ contatoPrincipal }}</span>
            <span>{{ oportunidade.funil.nome || "Funil não informado" }}</span>
            <span>{{ oportunidade.etapa || "Etapa não informada" }}</span>
          </div>
        </div>
        <div class="crm-hero-actions">
          <div class="crm-action-group">
            <span
              class="crm-status-pill"
              :class="statusClass(oportunidade.status)"
            >
              {{ statusLabel(oportunidade.status) }}
            </span>
            <button
              class="crm-action-button is-success"
              type="button"
              title="Marcar como ganha"
              @click="abrirModalGanho"
            >
              <i class="la la-check"></i>
              <span>Ganhar</span>
            </button>
            <button
              class="crm-action-button is-danger"
              type="button"
              title="Marcar como perdida"
              @click="abrirModalPerda"
            >
              <i class="la la-times"></i>
              <span>Perder</span>
            </button>
          </div>
          <div class="crm-action-group">
            <button
              class="crm-action-button"
              type="button"
              title="Editar dados"
              @click="abrirModalDados"
            >
              <i class="la la-edit"></i>
              <span>Dados</span>
            </button>
            <button
              class="crm-action-button"
              type="button"
              title="Criar tarefa"
              @click="abrirModalTarefa"
            >
              <i class="la la-check-square"></i>
              <span>Tarefa</span>
            </button>
            <button
              class="crm-action-button"
              type="button"
              title="Criar agendamento"
              @click="abrirModalAgenda()"
            >
              <i class="la la-calendar"></i>
              <span>Agenda</span>
            </button>
            <button
              class="crm-action-button"
              type="button"
              title="Adicionar nota"
              @click="focusNota"
            >
              <i class="la la-sticky-note"></i>
              <span>Nota</span>
            </button>
          </div>
          <div v-if="isContrato" class="crm-action-group">
            <button
              class="crm-action-button"
              type="button"
              title="Dados da negociação"
              @click="abrirDadosNegociacao"
            >
              <i class="la la-clipboard"></i>
              <span>Negociação</span>
            </button>
            <button
              class="crm-action-button"
              type="button"
              title="Enviar contrato por WhatsApp"
              :disabled="!whatsappUrl"
              @click="enviarContratoWhatsapp"
            >
              <i class="la la-whatsapp"></i>
              <span>WhatsApp</span>
            </button>
            <button
              class="crm-action-button"
              type="button"
              title="Enviar contrato por e-mail"
              :disabled="!contatoEmail"
              @click="enviarContratoEmail"
            >
              <i class="la la-envelope"></i>
              <span>E-mail</span>
            </button>
          </div>
        </div>
      </section>

      <div
        v-if="oportunidade.aguardando_assinatura_contrato"
        class="crm-flow-alert"
      >
        <i class="la la-file-signature"></i>
        <span>{{ oportunidade.aviso_fluxo }}</span>
      </div>

      <nav v-if="etapasFunilAtual.length" class="crm-stage-bar">
        <button
          v-for="(etapa, index) in etapasFunilAtual"
          :key="etapa.id_crm_etapa || etapa.nome"
          class="crm-stage-item"
          :class="{
            active: etapaAtualIndex === index,
            done: etapaAtualIndex > index,
          }"
          type="button"
          :disabled="
            stageChanging ||
            oportunidade.status !== 'aberta' ||
            etapa.id_crm_etapa === oportunidade.id_crm_etapa
          "
          @click="tentarMoverEtapaDetalhe(etapa)"
        >
          <span>{{ index + 1 }}</span>
          <strong>{{ etapa.nome }}</strong>
        </button>
      </nav>

      <div class="crm-opportunity-layout">
        <aside class="crm-opportunity-side">
          <section class="crm-panel">
            <div class="crm-panel-header">
              <h3>Dados principais</h3>
              <button
                class="btn btn-light-primary btn-xs"
                type="button"
                @click="abrirModalDados"
              >
                Editar
              </button>
            </div>
            <div class="crm-detail-row">
              <span>Status</span>
              <strong>{{ statusLabel(oportunidade.status) }}</strong>
            </div>
            <div class="crm-detail-row">
              <span>Funil</span>
              <strong>{{ oportunidade.funil.nome || "-" }}</strong>
            </div>
            <div class="crm-detail-row">
              <span>Etapa</span>
              <strong>{{ oportunidade.etapa || "-" }}</strong>
            </div>
            <template v-if="!ocultarValoresFinanceirosDetalhe">
              <div class="crm-detail-row">
                <span>Valor principal</span>
                <strong>{{ formatMoney(valorPrincipalDetalhe) }}</strong>
              </div>
              <div class="crm-detail-row">
                <span>Estimado</span>
                <strong>{{ formatMoney(oportunidade.valor_estimado) }}</strong>
              </div>
              <div class="crm-detail-row">
                <span>Proposta</span>
                <strong>{{ formatMoneyOrDash(valorPropostaDetalhe) }}</strong>
              </div>
              <div class="crm-detail-row">
                <span>Negociação</span>
                <strong>{{ formatMoneyOrDash(valorNegociacaoDetalhe) }}</strong>
              </div>
              <div class="crm-detail-row">
                <span>Final</span>
                <strong>{{ formatMoneyOrDash(valorFinalDetalhe) }}</strong>
              </div>
            </template>
            <div class="crm-detail-row">
              <span>Previsão</span>
              <strong>{{
                formatDate(oportunidade.previsao_fechamento)
              }}</strong>
            </div>
            <div class="crm-detail-row">
              <span>Próxima ação</span>
              <strong>{{
                formatDateTime(oportunidade.proxima_atividade_em)
              }}</strong>
            </div>
            <div class="crm-detail-row">
              <span>Temperatura</span>
              <button
                class="crm-temperature-button"
                :style="{
                  '--temperature-color': temperaturaColor(
                    oportunidade.temperatura
                  ),
                }"
                type="button"
                @click="abrirModalTemperaturaDetalhe"
              >
                <i :class="temperaturaIcon(oportunidade.temperatura)"></i>
                <strong>
                  {{ temperaturaLabel(oportunidade.temperatura) }}
                </strong>
              </button>
            </div>
          </section>

          <section class="crm-panel">
            <div class="crm-panel-header">
              <h3>Origem</h3>
            </div>
            <div class="crm-contact-card">
              <strong>{{ oportunidade.fonte || "Fonte não informada" }}</strong>
              <span>{{
                oportunidade.campanha || "Campanha não informada"
              }}</span>
            </div>
          </section>

          <section v-if="automacoesEtapa.length" class="crm-panel">
            <div class="crm-panel-header">
              <h3>Regras da etapa</h3>
            </div>
            <div class="crm-rule-list">
              <span
                v-for="regra in automacoesEtapa"
                :key="regra"
                class="crm-rule-pill"
              >
                {{ regra }}
              </span>
            </div>
          </section>

          <section v-if="camposComerciaisDetalhe.length" class="crm-panel">
            <div class="crm-panel-header">
              <h3>Campos comerciais</h3>
              <button
                class="btn btn-light-primary btn-xs"
                type="button"
                @click="abrirModalDados"
              >
                Editar
              </button>
            </div>
            <div
              v-for="campo in camposComerciaisDetalhe"
              :key="campo.id_crm_campo_comercial"
              class="crm-detail-row"
            >
              <span>{{ campo.nome }}</span>
              <strong>{{ campoValorLabel(campo) }}</strong>
            </div>
          </section>

          <section class="crm-panel">
            <div class="crm-panel-header">
              <h3>Contato</h3>
              <button
                class="btn btn-light-primary btn-xs"
                type="button"
                @click="abrirModalContato"
              >
                {{ oportunidade.id_crm_contato ? "Trocar" : "Adicionar" }}
              </button>
            </div>
            <div class="crm-contact-card">
              <strong>{{
                oportunidade.contato || "Contato CRM não vinculado"
              }}</strong>
              <span>{{ contatoTelefone || "Telefone não informado" }}</span>
              <span>{{ contatoEmail || "E-mail não informado" }}</span>
              <div class="crm-contact-actions">
                <button
                  class="btn btn-light btn-xs"
                  type="button"
                  :disabled="!contatoTelefone"
                  @click="copiarTelefoneContato"
                >
                  Copiar telefone
                </button>
                <button
                  class="btn btn-light-success btn-xs"
                  type="button"
                  :disabled="!whatsappUrl"
                  @click="abrirWhatsappContato"
                >
                  WhatsApp
                </button>
              </div>
            </div>
          </section>

          <section class="crm-panel">
            <div class="crm-panel-header">
              <h3>Empresa</h3>
              <button
                class="btn btn-light-primary btn-xs"
                type="button"
                @click="abrirModalEmpresa"
              >
                {{ oportunidade.id_pessoa ? "Trocar" : "Associar" }}
              </button>
            </div>
            <div class="crm-contact-card">
              <strong>{{ empresaLabel || "Empresa não vinculada" }}</strong>
              <span>{{ empresaDocumento || "Documento não informado" }}</span>
            </div>
          </section>

          <section class="crm-panel">
            <div class="crm-panel-header">
              <h3>Responsável</h3>
            </div>
            <div class="crm-contact-card">
              <strong>{{ oportunidade.usuario || "Sem responsável" }}</strong>
              <span>Responsável comercial</span>
            </div>
            <div class="crm-contact-card mt-3">
              <strong>{{ oportunidade.vendedor_responsavel || "-" }}</strong>
              <span>Vendedor fixo</span>
            </div>
            <div class="crm-contact-card mt-3">
              <strong>{{ oportunidade.responsavel_tecnico || "-" }}</strong>
              <span>Responsável técnico</span>
            </div>
          </section>
        </aside>

        <main class="crm-opportunity-main">
          <section class="crm-panel">
            <div class="crm-panel-header">
              <div>
                <h3>Próximas tarefas</h3>
                <span
                  >{{ proximasAcoesPendentesCount }} pendentes em destaque</span
                >
              </div>
              <button
                class="btn btn-light-primary btn-sm"
                type="button"
                @click="abrirModalTarefa"
              >
                Criar tarefa
              </button>
            </div>
            <CrmEmptyState
              v-if="listasLoading"
              title="Carregando ações..."
              description="Buscando tarefas e agendamentos desta oportunidade."
              icon="la la-refresh"
            />
            <CrmEmptyState
              v-else-if="
                proximasTarefas.length === 0 &&
                solicitacoesTecnicasPendentes.length === 0
              "
              title="Nenhuma tarefa pendente"
              description="Crie uma tarefa para manter o próximo passo claro."
              icon="la la-check-circle"
            >
              <template #action>
                <button
                  class="btn btn-light-primary btn-sm"
                  type="button"
                  @click="abrirModalTarefa"
                >
                  Criar tarefa
                </button>
              </template>
            </CrmEmptyState>
            <div v-else>
              <div
                v-if="solicitacoesTecnicasPendentes.length"
                class="crm-technical-request-list"
              >
                <article
                  v-for="solicitacao in solicitacoesTecnicasPendentes"
                  :key="solicitacao.id_crm_solicitacao_tecnica"
                  class="crm-technical-request"
                >
                  <div class="crm-technical-request-info">
                    <strong>Apresentação técnica</strong>
                    <span>
                      {{ solicitacaoTecnicaStatusLabel(solicitacao.status) }}
                      · {{ solicitacao.tecnico || "Técnico não definido" }}
                    </span>
                    <small v-if="solicitacao.agenda_data_inicio">
                      Agendado em
                      {{ formatDateTime(solicitacao.agenda_data_inicio) }}
                    </small>
                    <small v-else-if="solicitacao.link_url">
                      Link gerado para envio ao cliente
                    </small>
                    <small v-else>Solicitação aguardando agenda</small>
                  </div>
                  <div class="crm-technical-request-actions">
                    <button
                      class="crm-action-button is-small"
                      type="button"
                      title="Gerar link"
                      @click="abrirModalLinkSolicitacaoTecnica(solicitacao)"
                    >
                      <i class="la la-link"></i>
                      <span>Gerar</span>
                    </button>
                    <button
                      class="crm-action-button is-small"
                      type="button"
                      title="Copiar link"
                      :disabled="!linkPublicoSolicitacao(solicitacao)"
                      @click="copiarLinkSolicitacaoTecnica(solicitacao)"
                    >
                      <i class="la la-copy"></i>
                      <span>Copiar</span>
                    </button>
                    <button
                      class="crm-action-button is-small"
                      type="button"
                      title="Enviar por WhatsApp"
                      :disabled="
                        !linkPublicoSolicitacao(solicitacao) || !contatoTelefone
                      "
                      @click="enviarLinkTecnicoWhatsapp(solicitacao)"
                    >
                      <i class="la la-whatsapp"></i>
                      <span>WhatsApp</span>
                    </button>
                    <button
                      class="crm-action-button is-small"
                      type="button"
                      title="Enviar por e-mail"
                      :disabled="
                        !linkPublicoSolicitacao(solicitacao) || !contatoEmail
                      "
                      @click="enviarLinkTecnicoEmail(solicitacao)"
                    >
                      <i class="la la-envelope"></i>
                      <span>E-mail</span>
                    </button>
                    <button
                      class="crm-action-button is-small"
                      type="button"
                      title="Agendar direto"
                      @click="abrirAgendaSolicitacaoTecnica(solicitacao)"
                    >
                      <i class="la la-calendar"></i>
                      <span>Agendar</span>
                    </button>
                    <button
                      class="crm-action-button is-small"
                      type="button"
                      title="Ver horários"
                      @click="abrirAgendaSolicitacaoTecnica(solicitacao, true)"
                    >
                      <i class="la la-clock"></i>
                      <span>Horários</span>
                    </button>
                  </div>
                </article>
              </div>
              <div v-if="proximasTarefas.length" class="crm-task-list">
                <article
                  v-for="tarefa in proximasTarefas"
                  :key="tarefa.id_crm_atividade"
                  class="crm-task-item"
                  :class="{ late: atividadeAtrasada(tarefa) }"
                >
                  <div>
                    <strong>{{ tarefa.titulo }}</strong>
                    <span>
                      {{ tipoTarefaLabel(tarefa.tipo) }} ·
                      {{ formatDateTime(tarefa.vencimento_em) }}
                    </span>
                  </div>
                  <div class="crm-task-actions">
                    <button
                      v-if="isTarefaContrato(tarefa)"
                      class="btn btn-light-info btn-xs"
                      type="button"
                      @click="abrirDadosNegociacao"
                    >
                      Dados
                    </button>
                    <button
                      v-if="tarefa.status === 'aberta'"
                      class="btn btn-light btn-xs"
                      type="button"
                      @click="abrirEditarTarefa(tarefa)"
                    >
                      Editar
                    </button>
                    <button
                      v-if="tarefaTemAgendaAtiva(tarefa)"
                      class="btn btn-light-primary btn-xs"
                      type="button"
                      @click="abrirReagendamentoTarefa(tarefa)"
                    >
                      Reagendar
                    </button>
                    <button
                      v-else
                      class="btn btn-light-primary btn-xs"
                      type="button"
                      @click="abrirModalAgenda(tarefa)"
                    >
                      Agendar
                    </button>
                    <button
                      v-if="tarefa.status === 'aberta'"
                      class="btn btn-light-success btn-xs"
                      type="button"
                      @click="concluirTarefa(tarefa)"
                    >
                      Concluir
                    </button>
                  </div>
                </article>
              </div>
            </div>
          </section>

          <section class="crm-panel crm-tabs-panel">
            <b-tabs v-model="activeTab" nav-class="crm-tabs-nav">
              <b-tab title="Histórico">
                <div class="crm-tab-toolbar">
                  <strong>{{ timelineCountLabel }}</strong>
                  <button
                    class="btn btn-light btn-sm"
                    type="button"
                    @click="loadTimeline(true)"
                  >
                    Atualizar
                  </button>
                </div>
                <div class="crm-timeline-filters">
                  <button
                    v-for="filtro in timelineFiltroOptions"
                    :key="filtro.value"
                    class="crm-timeline-filter"
                    :class="{ active: timelineFiltro === filtro.value }"
                    type="button"
                    @click="alterarFiltroTimeline(filtro.value)"
                  >
                    <i :class="filtro.icon"></i>
                    <span>{{ filtro.text }}</span>
                  </button>
                  <button
                    class="crm-timeline-filter crm-timeline-upload"
                    type="button"
                    :disabled="timelineUploading"
                    @click="abrirUploadAnexoTimeline"
                  >
                    <i class="la la-paperclip"></i>
                    <span>
                      {{ timelineUploading ? "Enviando..." : "Anexar" }}
                    </span>
                  </button>
                  <input
                    ref="timelineAnexoInput"
                    class="d-none"
                    type="file"
                    @change="salvarAnexoTimeline"
                  />
                </div>
                <div class="crm-quick-note">
                  <b-form-textarea
                    v-model="timelineNotaRapida"
                    placeholder="Adicionar nota rápida ao histórico"
                    rows="2"
                  />
                  <button
                    class="btn btn-dark btn-sm"
                    type="button"
                    @click="salvarNotaRapida"
                  >
                    Salvar nota
                  </button>
                </div>
                <CrmEmptyState
                  v-if="timelineLoading"
                  title="Carregando timeline..."
                  description="Organizando eventos recentes desta oportunidade."
                  icon="la la-refresh"
                />
                <CrmEmptyState
                  v-else-if="timelineAgrupada.length === 0"
                  title="Nenhum evento encontrado"
                  description="Crie uma tarefa, agendamento ou nota para registrar o histórico da oportunidade."
                  icon="la la-history"
                >
                  <template #action>
                    <button
                      class="btn btn-light-primary btn-sm"
                      type="button"
                      @click="abrirModalTarefa"
                    >
                      Criar tarefa
                    </button>
                  </template>
                </CrmEmptyState>
                <div v-else>
                  <section
                    v-for="grupo in timelineAgrupada"
                    :key="grupo.key"
                    class="crm-timeline-group"
                  >
                    <div class="crm-timeline-group-title">
                      <span>{{ grupo.label }}</span>
                      <small>{{ grupo.items.length }} eventos</small>
                    </div>
                    <CrmTimelineItem
                      v-for="item in grupo.items"
                      :key="item.id_crm_timeline"
                      :title="timelineTitle(item.tipo_evento)"
                      :description="item.descricao"
                      :date="item.created_at"
                      :user="item.usuario || 'CRM'"
                      :type="timelineCategoriaLabel(item.tipo_evento)"
                      :status="timelineStatus(item.tipo_evento)"
                    >
                      <template #actions>
                        <a
                          v-if="timelineAnexoUrl(item)"
                          class="btn btn-light-primary btn-xs mt-2 mr-2"
                          :href="timelineAnexoUrl(item)"
                          target="_blank"
                          rel="noopener"
                        >
                          <i class="la la-download"></i>
                          Baixar {{ timelineAnexoNome(item) }}
                        </a>
                        <button
                          v-if="timelineAgendavel(item)"
                          class="btn btn-light-primary btn-xs mt-2"
                          type="button"
                          @click="agendarTarefaTimeline(item)"
                        >
                          Agendar tarefa
                        </button>
                      </template>
                    </CrmTimelineItem>
                  </section>
                </div>
                <div class="text-center mt-4" v-if="timelineHasMore">
                  <button
                    class="btn btn-light btn-sm"
                    type="button"
                    @click="loadTimeline(false)"
                  >
                    Carregar mais
                  </button>
                </div>
              </b-tab>

              <b-tab title="Tarefas">
                <div class="crm-tab-toolbar">
                  <strong>{{ atividades.length }} tarefas</strong>
                  <button
                    class="btn btn-light-primary btn-sm"
                    type="button"
                    @click="abrirModalTarefa"
                  >
                    Criar tarefa
                  </button>
                </div>
                <CrmEmptyState
                  v-if="atividades.length === 0"
                  title="Nenhuma tarefa criada"
                  description="Registre ligações, follow-ups e reuniões para acompanhar a negociação."
                  icon="la la-list"
                >
                  <template #action>
                    <button
                      class="btn btn-light-primary btn-sm"
                      type="button"
                      @click="abrirModalTarefa"
                    >
                      Criar tarefa
                    </button>
                  </template>
                </CrmEmptyState>
                <div v-else class="crm-task-list">
                  <article
                    v-for="tarefa in atividades"
                    :key="tarefa.id_crm_atividade"
                    class="crm-task-item"
                    :class="{ late: atividadeAtrasada(tarefa) }"
                  >
                    <div>
                      <strong>{{ tarefa.titulo }}</strong>
                      <span>
                        {{ tipoTarefaLabel(tarefa.tipo) }} ·
                        {{ atividadeStatusLabel(tarefa.status) }} ·
                        {{ formatDateTime(tarefa.vencimento_em) }}
                      </span>
                      <p v-if="tarefa.descricao">{{ tarefa.descricao }}</p>
                    </div>
                    <div class="crm-task-actions">
                      <button
                        v-if="tarefa.status === 'aberta'"
                        class="btn btn-light btn-xs"
                        type="button"
                        @click="abrirEditarTarefa(tarefa)"
                      >
                        Editar
                      </button>
                      <button
                        v-if="tarefaTemAgendaAtiva(tarefa)"
                        class="btn btn-light-primary btn-xs"
                        type="button"
                        @click="abrirReagendamentoTarefa(tarefa)"
                      >
                        Reagendar
                      </button>
                      <button
                        v-else
                        class="btn btn-light-primary btn-xs"
                        type="button"
                        @click="abrirModalAgenda(tarefa)"
                      >
                        Agendar
                      </button>
                      <button
                        v-if="tarefa.status === 'aberta'"
                        class="btn btn-light-success btn-xs"
                        type="button"
                        @click="concluirTarefa(tarefa)"
                      >
                        Concluir
                      </button>
                    </div>
                  </article>
                </div>
              </b-tab>

              <b-tab title="Agendamentos">
                <div class="crm-tab-toolbar">
                  <strong>{{ agendas.length }} agendamentos CRM</strong>
                  <button
                    class="btn btn-light-primary btn-sm"
                    type="button"
                    @click="abrirModalAgenda()"
                  >
                    Criar agendamento
                  </button>
                </div>
                <CrmEmptyState
                  v-if="agendas.length === 0"
                  title="Nenhum agendamento CRM"
                  description="Agende reuniões, visitas e retornos sem depender da agenda operacional."
                  icon="la la-calendar"
                >
                  <template #action>
                    <button
                      class="btn btn-light-primary btn-sm"
                      type="button"
                      @click="abrirModalAgenda()"
                    >
                      Criar agendamento
                    </button>
                  </template>
                </CrmEmptyState>
                <div v-else class="crm-task-list">
                  <article
                    v-for="agenda in agendas"
                    :key="agenda.id_crm_agenda"
                    class="crm-task-item"
                  >
                    <div>
                      <strong>{{ agenda.titulo }}</strong>
                      <span>
                        {{ tipoAgendaLabel(agenda.tipo) }} ·
                        {{ agendaStatusLabel(agenda.status) }} ·
                        {{ formatDateTime(agenda.data_inicio) }}
                      </span>
                      <p v-if="agenda.descricao">{{ agenda.descricao }}</p>
                    </div>
                    <div class="crm-task-actions">
                      <button
                        v-if="podeReagendarAgenda(agenda)"
                        class="btn btn-light-primary btn-xs"
                        type="button"
                        @click="abrirReagendamentoAgenda(agenda)"
                      >
                        Reagendar
                      </button>
                      <button
                        v-if="podeCancelarAgenda(agenda)"
                        class="btn btn-light-danger btn-xs"
                        type="button"
                        @click="cancelarAgenda(agenda)"
                      >
                        Cancelar
                      </button>
                      <button
                        v-if="podeConcluirAgenda(agenda)"
                        class="btn btn-light-success btn-xs"
                        type="button"
                        @click="alterarStatusAgenda(agenda, 'concluido')"
                      >
                        Concluir
                      </button>
                    </div>
                  </article>
                </div>
              </b-tab>

              <b-tab title="Notas">
                <div class="crm-note-box">
                  <b-form-textarea
                    ref="notaInput"
                    v-model="nota"
                    placeholder="Registre uma anotação para a oportunidade"
                    rows="4"
                  />
                  <div class="text-right mt-3">
                    <button
                      class="btn btn-dark btn-sm"
                      type="button"
                      @click="adicionarNota"
                    >
                      Salvar nota
                    </button>
                  </div>
                </div>
                <CrmEmptyState
                  v-if="notasTimeline.length === 0"
                  title="Nenhuma nota registrada"
                  description="Anotações ajudam a manter o histórico claro para todo o time."
                  icon="la la-sticky-note"
                />
                <div v-else class="mt-4">
                  <CrmTimelineItem
                    v-for="item in notasTimeline"
                    :key="item.id_crm_timeline"
                    :title="timelineTitle(item.tipo_evento)"
                    :description="item.descricao"
                    :date="item.created_at"
                    :user="item.usuario || 'CRM'"
                    :type="item.tipo_evento"
                  />
                </div>
              </b-tab>

              <b-tab title="Contatos">
                <div class="crm-contact-grid">
                  <div class="crm-contact-card">
                    <span>Contato CRM</span>
                    <strong>{{
                      oportunidade.contato || "Não vinculado"
                    }}</strong>
                    <span>{{
                      contatoTelefone || "Telefone não informado"
                    }}</span>
                    <span>{{ contatoEmail || "E-mail não informado" }}</span>
                    <div class="crm-contact-actions">
                      <button
                        class="btn btn-light btn-xs"
                        type="button"
                        :disabled="!contatoTelefone"
                        @click="copiarTelefoneContato"
                      >
                        Copiar
                      </button>
                      <button
                        class="btn btn-light-success btn-xs"
                        type="button"
                        :disabled="!whatsappUrl"
                        @click="abrirWhatsappContato"
                      >
                        WhatsApp
                      </button>
                      <button
                        class="btn btn-light-primary btn-xs"
                        type="button"
                        @click="abrirModalContato"
                      >
                        {{
                          oportunidade.id_crm_contato ? "Trocar" : "Adicionar"
                        }}
                      </button>
                    </div>
                  </div>
                  <div class="crm-contact-card">
                    <span>Pessoa/empresa</span>
                    <strong>{{ empresaLabel || "Não vinculada" }}</strong>
                    <span>{{
                      empresaDocumento || "Documento não informado"
                    }}</span>
                    <div class="crm-contact-actions">
                      <button
                        class="btn btn-light-primary btn-xs"
                        type="button"
                        @click="abrirModalEmpresa"
                      >
                        {{ oportunidade.id_pessoa ? "Trocar" : "Associar" }}
                      </button>
                    </div>
                  </div>
                </div>
                <CrmEmptyState
                  v-if="!oportunidade.contato && !oportunidade.pessoa"
                  title="Nenhum contato vinculado"
                  description="Esta oportunidade ainda não tem contato ou cadastro associado."
                  icon="la la-user"
                />
              </b-tab>
            </b-tabs>
          </section>
        </main>
      </div>
    </div>

    <CrmEmptyState
      v-else
      title="Não foi possível abrir o card"
      :description="
        erroOportunidade ||
        'Atualize a página ou tente abrir o card pelo funil novamente.'
      "
      icon="la la-exclamation-circle"
    />

    <b-sidebar
      id="crm-detalhe-tarefa-drawer"
      v-model="tarefaDrawerVisible"
      :title="tarefaDrawerTitle"
      backdrop
      right
      shadow
      width="520px"
    >
      <div class="crm-drawer-body">
        <b-form-group label="Título">
          <b-form-input v-model="tarefaForm.titulo" maxlength="140" />
        </b-form-group>
        <b-form-group label="Tipo">
          <b-form-select
            v-model="tarefaForm.tipo"
            :options="tipoTarefaOptions"
          />
        </b-form-group>
        <b-form-group label="Responsável">
          <b-form-select
            v-model="tarefaForm.id_usuario"
            :options="usuarioOptions"
          />
        </b-form-group>
        <b-form-group label="Vencimento">
          <b-form-input
            v-model="tarefaForm.vencimento_em"
            type="datetime-local"
          />
        </b-form-group>
        <b-form-group label="Descrição">
          <b-form-textarea v-model="tarefaForm.descricao" rows="3" />
        </b-form-group>
        <b-form-checkbox
          v-if="!tarefaEditando"
          v-model="tarefaForm.concluir_ao_criar"
          switch
        >
          Concluir ao criar
        </b-form-checkbox>
      </div>
      <template #footer>
        <div class="crm-drawer-footer">
          <button
            class="btn btn-light mr-2"
            type="button"
            @click="tarefaDrawerVisible = false"
          >
            Cancelar
          </button>
          <button
            class="btn btn-light-primary mr-2"
            type="button"
            @click="salvarTarefa(true)"
          >
            {{ tarefaEditando ? "Salvar e agendar" : "Criar e agendar" }}
          </button>
          <button
            class="btn btn-dark"
            type="button"
            @click="salvarTarefa(false)"
          >
            {{ tarefaEditando ? "Salvar alterações" : "Salvar tarefa" }}
          </button>
        </div>
      </template>
    </b-sidebar>

    <b-sidebar
      id="crm-detalhe-agenda-drawer"
      v-model="agendaDrawerVisible"
      :title="agendaDrawerTitle"
      backdrop
      right
      shadow
      width="560px"
    >
      <div class="crm-drawer-body">
        <div v-if="tarefaAgendamentoOrigem" class="crm-agenda-cliente mb-4">
          <span>Tarefa vinculada</span>
          <strong>{{ tarefaAgendamentoOrigem.titulo }}</strong>
        </div>
        <div class="crm-agenda-cliente mb-4">
          <span>Cliente ou contato</span>
          <strong>{{ agendaClienteLabel }}</strong>
        </div>
        <b-form-group label="Título">
          <b-form-input v-model="agendaForm.titulo" maxlength="140" />
        </b-form-group>
        <b-form-group label="Tipo">
          <b-form-select
            v-model="agendaForm.tipo"
            :options="tipoAgendaOptions"
          />
        </b-form-group>
        <b-form-group :label="agendaResponsavelLabel">
          <b-form-select
            v-model="agendaForm.id_usuario"
            :options="agendaResponsavelOptions"
          />
        </b-form-group>
        <b-row>
          <b-col md="6">
            <b-form-group label="Data e horário">
              <b-form-input
                v-model="agendaForm.data_inicio"
                type="datetime-local"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Duração em minutos">
              <b-form-input
                v-model="agendaForm.duracao_minutos"
                type="number"
                min="1"
                max="1440"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <div class="crm-agenda-horarios">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div>
              <strong>Horários disponíveis</strong>
              <small class="crm-agenda-context">
                {{ agendaHorariosContextoLabel }}
              </small>
            </div>
            <button
              class="btn btn-light-primary btn-sm"
              type="button"
              :disabled="loadingAgendaHorarios"
              @click="buscarHorariosAgenda"
            >
              Buscar horários
            </button>
          </div>
          <div v-if="agendaHorariosErro" class="alert alert-light-danger">
            {{ agendaHorariosErro }}
          </div>
          <div v-else-if="loadingAgendaHorarios" class="text-muted">
            Carregando horários...
          </div>
          <div v-else-if="agendaSlots.length === 0" class="text-muted">
            Busque os horários antes de marcar.
          </div>
          <div v-else class="crm-agenda-slots">
            <button
              v-for="slot in agendaSlots"
              :key="slot.inicio"
              class="crm-agenda-slot"
              :class="{
                selected: agendaSlotSelecionado(slot),
                unavailable: !slot.disponivel,
              }"
              type="button"
              :aria-pressed="agendaSlotSelecionado(slot)"
              :disabled="!slot.disponivel"
              @click="selecionarHorarioAgenda(slot)"
            >
              <strong>{{ formatTime(slot.inicio) }}</strong>
              <small>
                {{ slot.disponivel ? "Livre" : slot.motivo || "Ocupado" }}
              </small>
            </button>
          </div>
        </div>
        <b-form-group label="Descrição">
          <b-form-textarea v-model="agendaForm.descricao" rows="3" />
        </b-form-group>
      </div>
      <template #footer>
        <div class="crm-drawer-footer">
          <button
            class="btn btn-light"
            type="button"
            @click="agendaDrawerVisible = false"
          >
            Cancelar
          </button>
          <button class="btn btn-dark" type="button" @click="criarAgenda">
            Salvar agendamento
          </button>
        </div>
      </template>
    </b-sidebar>

    <b-modal
      id="crm-detalhe-reagendar-agenda-modal"
      title="Reagendar agendamento"
      ok-title="Reagendar"
      cancel-title="Cancelar"
      @ok="confirmarReagendamentoAgenda"
    >
      <b-form-group label="Nova data e horário">
        <b-form-input
          v-model="agendaReagendamentoForm.data_inicio"
          type="datetime-local"
        />
      </b-form-group>
      <b-form-group label="Duração em minutos">
        <b-form-input
          v-model.number="agendaReagendamentoForm.duracao_minutos"
          type="number"
          min="1"
          max="1440"
        />
      </b-form-group>
      <div class="crm-agenda-horarios mb-4">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div>
            <strong>Horários disponíveis</strong>
            <small class="crm-agenda-context">
              {{ agendaReagendamentoHorariosContextoLabel }}
            </small>
          </div>
          <button
            class="btn btn-light-primary btn-sm"
            type="button"
            :disabled="loadingAgendaReagendamentoHorarios"
            @click="buscarHorariosReagendamentoAgenda"
          >
            Buscar horários
          </button>
        </div>
        <div
          v-if="agendaReagendamentoHorariosErro"
          class="alert alert-light-danger"
        >
          {{ agendaReagendamentoHorariosErro }}
        </div>
        <div v-else-if="loadingAgendaReagendamentoHorarios" class="text-muted">
          Carregando horários...
        </div>
        <div
          v-else-if="agendaReagendamentoSlots.length === 0"
          class="text-muted"
        >
          Busque os horários antes de remarcar.
        </div>
        <div v-else class="crm-agenda-slots">
          <button
            v-for="slot in agendaReagendamentoSlots"
            :key="slot.inicio"
            class="crm-agenda-slot"
            :class="{
              selected: agendaReagendamentoSlotSelecionado(slot),
              unavailable: !slot.disponivel,
            }"
            type="button"
            :aria-pressed="agendaReagendamentoSlotSelecionado(slot)"
            :disabled="!slot.disponivel"
            @click="selecionarHorarioReagendamentoAgenda(slot)"
          >
            <strong>{{ formatTime(slot.inicio) }}</strong>
            <small>
              {{ slot.disponivel ? "Livre" : slot.motivo || "Ocupado" }}
            </small>
          </button>
        </div>
      </div>
      <b-form-group label="Motivo do reagendamento">
        <b-form-textarea
          v-model="agendaReagendamentoForm.motivo_reagendamento"
          maxlength="500"
          rows="3"
        />
      </b-form-group>
    </b-modal>

    <b-modal
      id="crm-detalhe-link-solicitacao-modal"
      title="Gerar link de agenda"
      hide-footer
      size="lg"
    >
      <div v-if="linkSolicitacaoOrigem" class="crm-link-modal">
        <div class="crm-link-summary">
          <span>Solicitação técnica</span>
          <strong>{{ oportunidade ? oportunidade.titulo : "" }}</strong>
          <small>
            {{ solicitacaoTecnicaStatusLabel(linkSolicitacaoOrigem.status) }}
            ·
            {{
              linkSolicitacaoOrigem.tecnico ||
              linkSolicitacaoOrigem.usuario_tecnico ||
              "Responsável padrão"
            }}
          </small>
        </div>

        <b-row>
          <b-col md="6">
            <b-form-group label="Data inicial">
              <b-form-input
                v-model="linkSolicitacaoForm.data_inicio"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Data final">
              <b-form-input
                v-model="linkSolicitacaoForm.data_fim"
                type="date"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="5">
            <b-form-group label="Duração em minutos">
              <b-form-input
                v-model="linkSolicitacaoForm.duracao_minutos"
                type="number"
                min="1"
                max="1440"
              />
            </b-form-group>
          </b-col>
          <b-col md="7">
            <b-form-group label="Observação para a tarefa">
              <b-form-textarea
                v-model="linkSolicitacaoForm.observacoes"
                maxlength="500"
                placeholder="Ex.: Cliente escolhe o melhor horário pelo link."
                rows="2"
                max-rows="4"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div v-if="linkSolicitacaoGerado" class="crm-link-result">
          <span>Link gerado</span>
          <div>
            <input :value="linkSolicitacaoGerado" readonly />
            <button
              class="crm-action-button"
              type="button"
              title="Copiar link"
              @click="copiarLinkSolicitacaoModal"
            >
              <i class="la la-copy"></i>
              <span>Copiar</span>
            </button>
          </div>
        </div>

        <div class="crm-link-modal-actions">
          <button
            class="btn btn-light mr-2"
            type="button"
            @click="$bvModal.hide('crm-detalhe-link-solicitacao-modal')"
          >
            Fechar
          </button>
          <button
            class="crm-action-button mr-2"
            type="button"
            :disabled="!linkSolicitacaoGerado || !contatoTelefone"
            @click="enviarLinkSolicitacaoModalWhatsapp"
          >
            <i class="la la-whatsapp"></i>
            <span>WhatsApp</span>
          </button>
          <button
            class="crm-action-button mr-2"
            type="button"
            :disabled="!linkSolicitacaoGerado || !contatoEmail"
            @click="enviarLinkSolicitacaoModalEmail"
          >
            <i class="la la-envelope"></i>
            <span>E-mail</span>
          </button>
          <button
            class="btn btn-dark"
            type="button"
            :disabled="linkSolicitacaoLoading"
            @click="confirmarGerarLinkSolicitacaoTecnica"
          >
            {{ linkSolicitacaoLoading ? "Gerando..." : "Gerar link" }}
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="crm-detalhe-dados-modal"
      title="Editar dados comerciais"
      hide-footer
      size="lg"
    >
      <b-form-group label="Título">
        <b-form-input v-model="dadosForm.titulo" maxlength="140" />
      </b-form-group>
      <b-row>
        <b-col md="6">
          <b-form-group label="Funil">
            <b-form-select
              v-model="dadosForm.id_crm_funil"
              :options="funilOptions"
              @change="onDadosFunilChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Etapa">
            <b-form-select
              v-model="dadosForm.id_crm_etapa"
              :options="etapaOptions(dadosForm.id_crm_funil)"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col v-if="!ocultarValoresFinanceirosDetalhe" md="6">
          <b-form-group label="Valor estimado">
            <money
              class="form-control"
              v-model="dadosForm.valor_estimado"
              v-bind="priceOptions"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Responsável">
            <b-form-select
              v-model="dadosForm.id_usuario"
              :options="usuarioOptions"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6">
          <b-form-group label="Previsão de fechamento">
            <b-form-input v-model="dadosForm.previsao_fechamento" type="date" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Próxima ação">
            <b-form-input
              v-model="dadosForm.proxima_atividade_em"
              type="datetime-local"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6">
          <b-form-group label="Fonte">
            <b-form-select
              v-model="dadosForm.id_crm_fonte"
              :options="fonteOptions"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Campanha">
            <b-form-input
              v-model="dadosForm.campanha"
              maxlength="120"
              placeholder="Ex.: Black Friday, indicação, WhatsApp"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group label="Temperatura/qualificação">
        <b-form-select
          v-model="dadosForm.temperatura"
          :options="temperaturaOptions"
        />
      </b-form-group>
      <div v-if="camposComerciaisDetalhe.length" class="crm-extra-fields">
        <h4>Campos comerciais</h4>
        <b-row>
          <b-col
            v-for="campo in camposComerciaisDetalhe"
            :key="campo.id_crm_campo_comercial"
            md="6"
          >
            <b-form-group :label="campo.nome">
              <template
                v-if="
                  campo.chave === 'beneficios' ||
                  campo.chave === 'beneficios_livres'
                "
              >
                <b-form-textarea
                  v-model="
                    dadosForm.campos_comerciais[campo.id_crm_campo_comercial]
                  "
                  rows="3"
                  max-rows="5"
                  placeholder="Digite descontos, parcelamentos, bônus ou observações combinadas."
                />
              </template>
              <template v-else-if="campo.chave === 'beneficios_estruturados'">
                <div class="crm-benefit-suggestions">
                  <button
                    v-for="beneficio in beneficioOptions"
                    :key="beneficio.value"
                    :class="[
                      'crm-benefit-chip',
                      {
                        active: beneficioCampoSelecionado(
                          campo.id_crm_campo_comercial,
                          beneficio.value
                        ),
                      },
                    ]"
                    type="button"
                    @click="
                      toggleBeneficioCampo(
                        campo.id_crm_campo_comercial,
                        beneficio.value
                      )
                    "
                  >
                    {{ beneficio.text }}
                  </button>
                </div>
              </template>
              <b-form-select
                v-else-if="campo.tipo === 'lista'"
                v-model="
                  dadosForm.campos_comerciais[campo.id_crm_campo_comercial]
                "
                :options="campoComercialOptions(campo)"
              />
              <b-form-checkbox
                v-else-if="campo.tipo === 'booleano'"
                v-model="
                  dadosForm.campos_comerciais[campo.id_crm_campo_comercial]
                "
                value="1"
                unchecked-value="0"
              >
                Sim
              </b-form-checkbox>
              <b-form-input
                v-else
                v-model="
                  dadosForm.campos_comerciais[campo.id_crm_campo_comercial]
                "
                :type="campoInputType(campo)"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>
      <div class="d-flex justify-content-end">
        <button
          class="btn btn-light mr-2"
          type="button"
          @click="$bvModal.hide('crm-detalhe-dados-modal')"
        >
          Cancelar
        </button>
        <button class="btn btn-dark" type="button" @click="salvarDados">
          Salvar alterações
        </button>
      </div>
    </b-modal>

    <b-modal
      id="crm-detalhe-temperatura-modal"
      title="Temperatura da oportunidade"
      @ok.prevent="salvarTemperaturaDetalhe"
    >
      <b-form-group label="Oportunidade">
        <b-form-input
          :value="oportunidade ? oportunidade.titulo : ''"
          disabled
        />
      </b-form-group>
      <div class="crm-temperature-grid">
        <button
          v-for="option in temperaturaOptions"
          :key="String(option.value)"
          class="crm-temperature-option"
          :class="{ active: temperaturaForm.temperatura === option.value }"
          :style="{ '--temperature-color': temperaturaColor(option.value) }"
          type="button"
          @click="temperaturaForm.temperatura = option.value"
        >
          <i :class="temperaturaIcon(option.value)"></i>
          <span>{{ option.text }}</span>
        </button>
      </div>
    </b-modal>

    <b-modal
      id="crm-detalhe-mover-etapa-modal"
      title="Mover oportunidade"
      @ok.prevent="confirmarMoverEtapaDetalhe"
    >
      <b-form-group label="Etapa de destino">
        <b-form-select
          v-model="idEtapaDestino"
          :options="
            etapaOptions(oportunidade ? oportunidade.id_crm_funil : null)
          "
          @change="prepararCamposMovimento"
        />
      </b-form-group>
      <b-form-group
        v-if="movimentoExigeTecnico"
        label="Desenvolvedor da apresentação"
      >
        <b-form-select
          v-model="idUsuarioTecnicoDestino"
          :options="desenvolvedorResponsavelOptions"
        />
        <small class="text-muted">
          Este desenvolvedor assume a apresentação técnica e a implementação
          White Label.
        </small>
      </b-form-group>
      <b-form-group
        v-if="movimentoExigeAtribuicao"
        label="Vendedor responsável"
      >
        <b-form-select
          v-model="idUsuarioDestino"
          :options="vendedorResponsavelOptions"
        />
        <small class="text-muted">
          Este vendedor ficará vinculado ao card durante todo o fluxo comercial.
        </small>
        <b-form-checkbox
          v-if="usuarioDestinoSemAcesso"
          v-model="concederAcessoFunilDestino"
          class="mt-3"
        >
          Conceder acesso deste usuário ao funil atual
        </b-form-checkbox>
      </b-form-group>
      <div
        v-if="camposObrigatoriosMovimento.length"
        class="crm-move-requirements"
      >
        <b-alert show variant="warning" class="mb-4">
          Esta etapa exige informações antes de receber a oportunidade.
        </b-alert>
        <b-form-group
          v-if="movimentoExigeValorNegociacao"
          label="Valor negociado"
        >
          <b-form-radio-group
            v-model="movimentoManterValor"
            :options="[
              { value: true, text: 'Manter o valor já informado' },
              { value: false, text: 'Informar novo valor' },
            ]"
            stacked
            @change="prepararCamposMovimento"
          />
        </b-form-group>
        <b-form-group
          v-for="regra in camposObrigatoriosMovimento"
          :key="regra.id_crm_etapa_campo_obrigatorio"
          :label="labelCampoObrigatorio(regra)"
        >
          <b-form-select
            v-if="campoObrigatorioTipo(regra) === 'lista'"
            v-model="movimentoCamposForm[regra.id_crm_campo_comercial]"
            :options="campoObrigatorioOptions(regra)"
          />
          <b-form-textarea
            v-else-if="campoObrigatorioTipo(regra) === 'texto_longo'"
            v-model="movimentoCamposForm[regra.id_crm_campo_comercial]"
            rows="3"
          />
          <money
            v-else-if="
              campoObrigatorioTipo(regra) === 'numero' &&
              isCampoObrigatorioMonetario(regra)
            "
            v-model="movimentoCamposForm[regra.id_crm_campo_comercial]"
            class="form-control"
            v-bind="priceOptions"
            :disabled="
              movimentoManterValor &&
              campoObrigatorioChave(regra) === 'valor_negociacao'
            "
          />
          <b-form-input
            v-else-if="campoObrigatorioTipo(regra) === 'numero'"
            v-model="movimentoCamposForm[regra.id_crm_campo_comercial]"
            type="number"
            step="0.01"
            :disabled="
              movimentoManterValor &&
              campoObrigatorioChave(regra) === 'valor_negociacao'
            "
          />
          <b-form-input
            v-else
            v-model="movimentoCamposForm[regra.id_crm_campo_comercial]"
          />
          <small v-if="regra.mensagem" class="text-muted">
            {{ regra.mensagem }}
          </small>
        </b-form-group>
      </div>
    </b-modal>

    <b-modal
      id="crm-detalhe-contato-modal"
      title="Contato da oportunidade"
      hide-footer
    >
      <BuscaClienteContato
        v-model="contatoSelecionado"
        label="Contato principal"
        :incluir-pessoas="false"
        @criar-contato="criarContatoRapidoOportunidade"
      />
      <div class="d-flex justify-content-end">
        <button
          class="btn btn-light mr-2"
          type="button"
          @click="$bvModal.hide('crm-detalhe-contato-modal')"
        >
          Cancelar
        </button>
        <button
          class="btn btn-dark"
          type="button"
          @click="salvarContatoOportunidade"
        >
          Salvar contato
        </button>
      </div>
    </b-modal>

    <b-modal
      id="crm-detalhe-empresa-modal"
      title="Associar empresa/pessoa"
      hide-footer
    >
      <BuscaClienteContato
        v-model="empresaSelecionada"
        label="Pessoa existente"
        placeholder="Busque pelo nome, fantasia ou documento"
        :incluir-contatos="false"
        :permite-criar-contato="false"
      />
      <div class="d-flex justify-content-end">
        <button
          class="btn btn-light mr-2"
          type="button"
          @click="$bvModal.hide('crm-detalhe-empresa-modal')"
        >
          Cancelar
        </button>
        <button
          class="btn btn-dark"
          type="button"
          @click="salvarEmpresaOportunidade"
        >
          Associar
        </button>
      </div>
    </b-modal>

    <b-modal
      id="crm-detalhe-perda-modal"
      title="Marcar como perdida"
      @ok.prevent="marcarPerdida"
    >
      <b-form-group label="Motivo da perda">
        <b-form-select
          v-model="perdaForm.id_crm_motivo_perda"
          :options="motivoPerdaOptions"
        />
      </b-form-group>
      <b-form-group label="Observação">
        <b-form-textarea v-model="perdaForm.motivo_perda_observacao" rows="3" />
      </b-form-group>
    </b-modal>

    <b-modal
      id="crm-detalhe-ganho-modal"
      title="Fechar oportunidade"
      cancel-title="Cancelar"
      ok-title="Confirmar"
      size="xl"
      @ok.prevent="marcarGanha"
    >
      <div class="crm-close-summary">
        <strong>{{ ganhoForm.titulo }}</strong>
        <span>{{ ganhoForm.cliente }}</span>
      </div>
      <b-row>
        <b-col md="6">
          <b-form-group label="Nome correto">
            <b-form-input v-model="ganhoForm.nome_correto" maxlength="120" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="CNPJ">
            <b-input-group>
              <b-form-input v-model="ganhoForm.cnpj" maxlength="18" />
              <b-input-group-append>
                <button
                  class="btn btn-light-primary"
                  type="button"
                  :disabled="loadingConsultaCnpjFechamento"
                  @click="consultarCnpjFechamento"
                >
                  <i class="la la-search"></i>
                  Consultar
                </button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6">
          <b-form-group label="E-mail">
            <b-form-input
              v-model="ganhoForm.email_cliente"
              type="email"
              maxlength="120"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Telefone">
            <b-form-input v-model="ganhoForm.telefone_cliente" maxlength="20" />
          </b-form-group>
        </b-col>
      </b-row>
      <div class="crm-close-address">
        <b-row>
          <b-col md="5">
            <b-form-group label="CEP">
              <b-input-group>
                <b-form-input v-model="ganhoForm.cep" maxlength="9" />
                <b-input-group-append>
                  <button
                    class="btn btn-light-primary"
                    type="button"
                    :disabled="loadingConsultaCepFechamento"
                    @click="consultarCepFechamento"
                  >
                    <i class="la la-search"></i>
                    Consultar
                  </button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="7">
            <b-form-group label="Cidade">
              <b-form-input :value="cidadeFechamentoLabel" disabled />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="8">
            <b-form-group label="Logradouro">
              <b-form-input v-model="ganhoForm.logradouro" maxlength="100" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Número">
              <b-form-input v-model="ganhoForm.numero" maxlength="30" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="Bairro">
              <b-form-input v-model="ganhoForm.bairro" maxlength="100" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Complemento">
              <b-form-input v-model="ganhoForm.complemento" maxlength="100" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group label="Endereço completo">
          <b-form-input v-model="ganhoForm.endereco_cliente" maxlength="255" />
        </b-form-group>
      </div>
      <b-form-group label="Valor final">
        <money
          class="form-control"
          v-model="ganhoForm.valor_final"
          v-bind="priceOptions"
        />
      </b-form-group>
      <b-form-group label="Benefícios digitáveis">
        <b-form-textarea
          v-model="ganhoForm.beneficios_livres"
          rows="3"
          max-rows="5"
          placeholder="Digite descontos, parcelamentos, bônus ou observações combinadas."
        />
      </b-form-group>
      <b-form-group v-if="!ganhoForm.cliente_final" label="Benefícios de regra">
        <div class="crm-benefit-suggestions">
          <button
            v-for="beneficio in beneficioOptions"
            :key="beneficio.value"
            :class="[
              'crm-benefit-chip',
              { active: beneficioEstruturadoSelecionado(beneficio.value) },
            ]"
            type="button"
            @click="toggleBeneficioGanho(beneficio.value)"
          >
            {{ beneficio.text }}
          </button>
        </div>
      </b-form-group>
      <small class="text-muted">
        Ao confirmar, o CRM salva os dados do cliente, registra os benefícios na
        oportunidade e cria a entrada no funil Contratos.
      </small>
    </b-modal>

    <b-modal
      id="crm-detalhe-dados-negociacao-modal"
      title="Dados da negociação"
      hide-footer
    >
      <div class="crm-negotiation-data">
        <div>
          <span>Cliente</span>
          <strong>{{ contatoPrincipal }}</strong>
        </div>
        <div>
          <span>Negociação original</span>
          <strong>
            {{ oportunidade.oportunidade_origem || oportunidade.titulo }}
          </strong>
        </div>
        <div>
          <span>Valor negociado</span>
          <strong>{{ formatMoney(valorPrincipalDetalhe) }}</strong>
        </div>
        <div>
          <span>Telefone</span>
          <strong>{{ contatoTelefone || "Não informado" }}</strong>
        </div>
        <div>
          <span>E-mail</span>
          <strong>{{ contatoEmail || "Não informado" }}</strong>
        </div>
        <div v-for="campo in camposComerciaisDetalhe" :key="campo.chave">
          <span>{{ campo.nome }}</span>
          <strong>{{ campoValorLabel(campo) }}</strong>
        </div>
      </div>
      <div class="d-flex justify-content-end mt-4">
        <button
          class="btn btn-light"
          type="button"
          @click="$bvModal.hide('crm-detalhe-dados-negociacao-modal')"
        >
          Fechar
        </button>
      </div>
    </b-modal>

    <b-modal
      id="crm-detalhe-responsaveis-contrato-modal"
      title="Responsáveis pós-contrato"
      :ok-disabled="contratoResponsaveisLoading"
      :cancel-disabled="contratoResponsaveisLoading"
      ok-title="Concluir contrato"
      cancel-title="Cancelar"
      @ok.prevent="confirmarConclusaoContrato"
    >
      <div class="crm-contract-destination-summary">
        <div v-if="contratoExigeImplementacaoWhiteLabel">
          <span>Implementação White Label</span>
          <strong>{{ contratoResponsavelWhiteLabelLabel }}</strong>
        </div>
        <div v-if="contratoExigeTreinamentoClienteFinal">
          <span>Treinamento Cliente Final</span>
          <strong>{{ contratoTreinamentoClienteFinalSelecionadoLabel }}</strong>
        </div>
        <div v-if="contratoExigeTreinamento">
          <span>Treinamento</span>
          <strong>{{ contratoTreinamentoSelecionadoLabel }}</strong>
        </div>
        <div v-if="contratoExigeWebsite">
          <span>Website</span>
          <strong>{{ contratoWebsiteSelecionadoLabel }}</strong>
        </div>
      </div>
      <div class="crm-contract-responsibles-form">
        <label v-if="contratoExigeImplementacaoWhiteLabel">
          <span>Responsável pela implementação White Label</span>
          <select
            v-model.number="contratoResponsaveisForm.id_usuario_white_label"
            class="form-control"
          >
            <option :value="null" disabled>Selecione o responsável</option>
            <option
              v-for="option in desenvolvedorResponsavelOptions"
              :key="`white-label-${option.value}`"
              :value="option.value"
            >
              {{ option.text }}
            </option>
          </select>
          <small v-if="desenvolvedorResponsavelOptions.length === 0">
            Nenhum desenvolvedor disponível nas atribuições.
          </small>
        </label>

        <label v-if="contratoExigeTreinamentoClienteFinal">
          <span>Responsável pelo Treinamento Cliente Final</span>
          <select
            v-model.number="
              contratoResponsaveisForm.id_usuario_treinamento_cliente_final
            "
            class="form-control"
          >
            <option :value="null" disabled>Selecione o responsável</option>
            <option
              v-for="option in treinamentoClienteFinalResponsavelOptions"
              :key="`treinamento-cliente-final-${option.value}`"
              :value="option.value"
            >
              {{ option.text }}
            </option>
          </select>
          <small v-if="treinamentoClienteFinalResponsavelOptions.length === 0">
            Nenhum usuário de Treinamento Cliente Final disponível nas
            atribuições.
          </small>
        </label>

        <label v-if="contratoExigeTreinamento">
          <span>Responsável pelo treinamento</span>
          <select
            v-model.number="contratoResponsaveisForm.id_usuario_treinamento"
            class="form-control"
          >
            <option :value="null" disabled>Selecione o responsável</option>
            <option
              v-for="option in treinamentoResponsavelOptions"
              :key="`treinamento-${option.value}`"
              :value="option.value"
            >
              {{ option.text }}
            </option>
          </select>
          <small v-if="treinamentoResponsavelOptions.length === 0">
            Nenhum usuário de treinamento disponível nas atribuições.
          </small>
        </label>

        <label v-if="contratoExigeWebsite">
          <span>Responsável pelo website</span>
          <select
            v-model.number="contratoResponsaveisForm.id_usuario_website"
            class="form-control"
          >
            <option :value="null" disabled>Selecione o responsável</option>
            <option
              v-for="option in websiteResponsavelOptions"
              :key="`website-${option.value}`"
              :value="option.value"
            >
              {{ option.text }}
            </option>
          </select>
          <small v-if="websiteResponsavelOptions.length === 0">
            Nenhum usuário de website disponível nas atribuições.
          </small>
        </label>
      </div>
    </b-modal>
  </div>
</template>

<script>
import BuscaClienteContato from "@/modules/CRM/components/BuscaClienteContato";
import CrmEmptyState from "@/modules/CRM/components/CrmEmptyState";
import CrmPageHeader from "@/modules/CRM/components/CrmPageHeader";
import CrmTimelineItem from "@/modules/CRM/components/CrmTimelineItem";
import crmService from "@/modules/CRM/services/crmService";
import { SistemaMixin, priceOptions } from "@/modules/Application";
import { Money } from "v-money";

const TIMELINE_LIMIT = 20;

export default {
  name: "crm-oportunidade-detalhe",
  components: {
    BuscaClienteContato,
    CrmEmptyState,
    CrmPageHeader,
    CrmTimelineItem,
    Money,
  },
  mixins: [SistemaMixin],
  data() {
    return {
      activeTab: 0,
      priceOptions,
      agendas: [],
      agendaDrawerVisible: false,
      agendaForm: this.emptyAgendaForm(),
      agendaHorarios: null,
      agendaHorariosErro: "",
      agendaReagendamento: null,
      agendaReagendamentoForm: this.emptyAgendaReagendamentoForm(),
      agendaReagendamentoHorarios: null,
      agendaReagendamentoHorariosErro: "",
      agendaReagendamentoSelecionandoSlot: false,
      agendaSolicitacaoOrigem: null,
      atividades: [],
      contatoSelecionado: null,
      contratoResponsaveisForm: this.emptyContratoResponsaveisForm(),
      contratoResponsaveisLoading: false,
      contratoResponsaveisTarefa: null,
      dadosForm: this.emptyDadosForm(),
      empresaSelecionada: null,
      erroOportunidade: "",
      fontes: [],
      funis: [],
      listasLoading: false,
      loading: false,
      loadingAgendaHorarios: false,
      loadingAgendaReagendamentoHorarios: false,
      loadingConsultaCepFechamento: false,
      loadingConsultaCnpjFechamento: false,
      linkSolicitacaoForm: this.emptyLinkSolicitacaoForm(),
      linkSolicitacaoGerado: "",
      linkSolicitacaoLoading: false,
      linkSolicitacaoOrigem: null,
      motivosPerda: [],
      nota: "",
      oportunidade: null,
      stageChanging: false,
      solicitacoesTecnicas: [],
      idEtapaDestino: null,
      idUsuarioTecnicoDestino: null,
      idUsuarioDestino: null,
      concederAcessoFunilDestino: false,
      movimentoCamposForm: {},
      movimentoManterValor: true,
      temperaturaForm: {
        temperatura: null,
      },
      ganhoForm: this.emptyGanhoForm(),
      perdaForm: this.emptyPerdaForm(),
      tiposTarefa: [],
      tarefaEditando: null,
      tarefaForm: this.emptyTarefaForm(),
      tarefaAgendamentoOrigem: null,
      tarefaDrawerVisible: false,
      timeline: [],
      timelineFiltro: "todos",
      timelineNotaRapida: "",
      timelineHasMore: false,
      timelineLoading: false,
      timelineUploading: false,
      usuarios: [],
      vendedoresAtribuicao: [],
      desenvolvedoresAtribuicao: [],
      treinamentoAtribuicao: [],
      treinamentoClienteFinalAtribuicao: [],
      websiteAtribuicao: [],
      timelineFiltroOptions: [
        { value: "todos", text: "Todos", icon: "la la-stream" },
        { value: "alteracoes", text: "Alterações", icon: "la la-exchange" },
        { value: "notas", text: "Notas", icon: "la la-sticky-note" },
        { value: "tarefas", text: "Tarefas", icon: "la la-check-square" },
        { value: "agenda", text: "Agenda", icon: "la la-calendar" },
        { value: "contato", text: "Contato", icon: "la la-user" },
        { value: "whatsapp", text: "WhatsApp", icon: "la la-whatsapp" },
        { value: "anexos", text: "Anexos", icon: "la la-paperclip" },
      ],
      tipoAgendaOptions: [
        { value: "follow_up", text: "Follow-up" },
        { value: "reuniao", text: "Reunião" },
        { value: "visita", text: "Visita" },
        { value: "demonstracao", text: "Demonstração" },
        { value: "apresentacao_tecnica", text: "Apresentação técnica" },
        { value: "treinamento", text: "Treinamento" },
        {
          value: "treinamento_cliente_final",
          text: "Treinamento Cliente Final",
        },
        { value: "outro", text: "Outro" },
      ],
      temperaturaOptions: [
        { value: null, text: "Sem temperatura" },
        { value: "muito_frio", text: "Muito frio" },
        { value: "frio", text: "Frio" },
        { value: "morno", text: "Morno" },
        { value: "quente", text: "Quente" },
        { value: "venda_certa", text: "Venda certa" },
      ],
      beneficioOptions: [
        { value: "website", text: "Website" },
        {
          value: "implementacao_white_label",
          text: "Implementação White Label",
        },
        {
          value: "treinamento_cliente_final",
          text: "Treinamento Cliente Final",
        },
      ],
    };
  },
  computed: {
    idOportunidade() {
      return this.$route && this.$route.params ? this.$route.params.id : null;
    },
    backLabel() {
      if (this.$route.query.origem === "ganhas") {
        return "Visão geral";
      }

      return this.$route.query.origem === "funil" ? "Funil" : "Oportunidades";
    },
    backTo() {
      if (this.$route.query.origem === "ganhas") {
        return { name: "crmDashboard" };
      }

      return this.$route.query.origem === "funil"
        ? { name: "crmFunil" }
        : { name: "crmOportunidades" };
    },
    contatoPrincipal() {
      if (!this.oportunidade) {
        return "Sem cliente ou contato";
      }

      return (
        this.oportunidade.contato ||
        this.oportunidade.pessoa ||
        "Sem cliente ou contato"
      );
    },
    nomeClienteFechamento() {
      if (!this.oportunidade) {
        return "";
      }

      return (
        this.oportunidade.contato ||
        this.oportunidade.pessoa ||
        this.oportunidade.titulo ||
        ""
      );
    },
    documentoClienteFechamento() {
      if (!this.oportunidade) {
        return "";
      }

      return (
        this.oportunidade.contato_documento ||
        this.oportunidade.pessoa_cnpj_cpf ||
        this.oportunidade.pessoa_cpf ||
        ""
      );
    },
    contatoTelefone() {
      return this.oportunidade
        ? this.oportunidade.contato_telefone ||
            this.oportunidade.telefone_contato ||
            this.oportunidade.pessoa_telefone ||
            ""
        : "";
    },
    camposComerciaisDetalhe() {
      if (
        !this.oportunidade ||
        !Array.isArray(this.oportunidade.campos_comerciais)
      ) {
        return [];
      }

      if (this.ocultarValoresFinanceirosDetalhe) {
        return this.oportunidade.campos_comerciais.filter(
          (campo) =>
            !["valor_proposta", "valor_negociacao", "valor_final"].includes(
              campo.chave
            )
        );
      }

      return this.oportunidade.campos_comerciais;
    },
    ocultarValoresFinanceirosDetalhe() {
      return ["treinamento", "website"].includes(
        this.oportunidade ? this.oportunidade.tipo_fluxo : null
      );
    },
    valorPropostaDetalhe() {
      return this.valorCampoComercial(this.oportunidade, "valor_proposta");
    },
    valorNegociacaoDetalhe() {
      return this.valorCampoComercial(this.oportunidade, "valor_negociacao");
    },
    valorFinalDetalhe() {
      return this.valorCampoComercial(this.oportunidade, "valor_final");
    },
    valorPrincipalDetalhe() {
      return this.valorPrincipalOportunidade(this.oportunidade);
    },
    cidadeFechamentoLabel() {
      return [this.ganhoForm.cidade, this.ganhoForm.uf]
        .filter(Boolean)
        .join(" / ");
    },
    contatoEmail() {
      return this.oportunidade
        ? this.oportunidade.contato_email ||
            this.oportunidade.pessoa_email ||
            ""
        : "";
    },
    isContrato() {
      return this.oportunidade && this.oportunidade.tipo_fluxo === "contratos";
    },
    whatsappUrl() {
      const telefone = this.normalizarTelefoneWhatsapp(this.contatoTelefone);

      return telefone ? `https://web.whatsapp.com/send?phone=${telefone}` : "";
    },
    empresaLabel() {
      if (!this.oportunidade) {
        return "";
      }

      return (
        this.oportunidade.pessoa || this.oportunidade.pessoa_nome_fantasia || ""
      );
    },
    empresaDocumento() {
      if (!this.oportunidade) {
        return "";
      }

      return this.oportunidade.pessoa_cnpj_cpf || this.oportunidade.pessoa_cpf;
    },
    etapasFunilAtual() {
      if (!this.oportunidade) {
        return [];
      }

      const funil = this.funis.find(
        (item) => item.id_crm_funil === this.oportunidade.id_crm_funil
      );

      if (funil && Array.isArray(funil.etapas) && funil.etapas.length) {
        return funil.etapas;
      }

      return this.oportunidade.etapa
        ? [
            {
              id_crm_etapa: this.oportunidade.id_crm_etapa,
              nome: this.oportunidade.etapa,
            },
          ]
        : [];
    },
    etapaAtualIndex() {
      return this.etapasFunilAtual.findIndex(
        (etapa) => etapa.id_crm_etapa === this.oportunidade.id_crm_etapa
      );
    },
    etapaAtualConfig() {
      return this.etapasFunilAtual.find(
        (etapa) => etapa.id_crm_etapa === this.oportunidade.id_crm_etapa
      );
    },
    automacoesEtapa() {
      const etapa = this.etapaAtualConfig || {};
      const regras = [];

      if (etapa.sugerir_tarefa) {
        regras.push(
          etapa.tarefa_sugerida_titulo || "Sugerir tarefa ao entrar na etapa"
        );
      }

      if (etapa.destacar_sem_tarefa) {
        regras.push("Destacar oportunidade sem tarefa");
      }

      if (etapa.alertar_oportunidade_parada) {
        regras.push(
          `Alertar parada há ${etapa.alerta_parada_dias || 7} dia(s)`
        );
      }

      if (etapa.exigir_motivo_perda) {
        regras.push("Exigir motivo de perda");
      }

      return regras;
    },
    atividadesPendentes() {
      return this.atividades
        .filter((atividade) => atividade.status !== "concluida")
        .slice()
        .sort((a, b) => {
          const dataA = a.vencimento_em
            ? new Date(a.vencimento_em)
            : new Date(0);
          const dataB = b.vencimento_em
            ? new Date(b.vencimento_em)
            : new Date(0);
          return dataA - dataB;
        });
    },
    proximasTarefas() {
      return this.atividadesPendentes.slice(0, 3);
    },
    solicitacoesTecnicasPendentes() {
      return this.solicitacoesTecnicas.filter(
        (solicitacao) => !this.solicitacaoTecnicaFinalizada(solicitacao)
      );
    },
    proximasAcoesPendentesCount() {
      return (
        this.proximasTarefas.length + this.solicitacoesTecnicasPendentes.length
      );
    },
    notasTimeline() {
      return this.timeline.filter((item) =>
        ["nota", "nota_adicionada"].includes(item.tipo_evento)
      );
    },
    timelineAgrupada() {
      return this.agruparTimelinePorDia(this.timeline);
    },
    timelineCountLabel() {
      const total = this.timeline.length;
      const filtro = this.timelineFiltroOptions.find(
        (item) => item.value === this.timelineFiltro
      );
      const label = filtro ? filtro.text.toLowerCase() : "eventos";

      return `${total} ${total === 1 ? "evento" : "eventos"} · ${label}`;
    },
    funilOptions() {
      return this.funis.map((funil) => ({
        value: funil.id_crm_funil,
        text: funil.nome,
      }));
    },
    fonteOptions() {
      return [{ value: null, text: "Sem fonte" }].concat(
        this.fontes.map((fonte) => ({
          value: fonte.id_crm_fonte,
          text: fonte.nome,
        }))
      );
    },
    usuarioOptions() {
      return [{ value: null, text: "Responsável padrão" }].concat(
        this.usuarioOptionsSemTodos
      );
    },
    usuarioOptionsSemTodos() {
      return this.usuarios.map((usuario) => ({
        value: usuario.id_usuario,
        text: usuario.nome || usuario.usuario,
      }));
    },
    agendaResponsavelOptions() {
      return this.isTreinamentoClienteFinal(this.oportunidade)
        ? this.treinamentoResponsavelOptions
        : this.usuarioOptions;
    },
    agendaDrawerTitle() {
      return this.isTreinamentoClienteFinal(this.oportunidade)
        ? "Agendar treinamento"
        : "Criar agendamento CRM";
    },
    tarefaDrawerTitle() {
      return this.tarefaEditando ? "Editar tarefa" : "Criar tarefa";
    },
    agendaResponsavelLabel() {
      return this.isTreinamentoClienteFinal(this.oportunidade)
        ? "Responsável pelo treinamento"
        : "Responsável";
    },
    agendaHorariosContextoLabel() {
      const responsavel =
        this.optionText(
          this.agendaResponsavelOptions,
          this.agendaForm.id_usuario
        ) || "responsável não selecionado";
      const data = this.dateFromDateTimeLocal(this.agendaForm.data_inicio);
      const dataLabel = data
        ? new Date(`${data}T00:00:00`).toLocaleDateString("pt-BR")
        : "data não informada";

      return `${responsavel} em ${dataLabel}`;
    },
    agendaReagendamentoSlots() {
      return this.agendaReagendamentoHorarios
        ? this.agendaReagendamentoHorarios.slots || []
        : [];
    },
    agendaReagendamentoHorariosContextoLabel() {
      const responsavel =
        this.optionText(
          this.usuarioOptionsSemTodos,
          this.agendaReagendamentoResponsavelId()
        ) || "responsável não selecionado";
      const data = this.dateFromDateTimeLocal(
        this.agendaReagendamentoForm.data_inicio
      );
      const dataLabel = data
        ? new Date(`${data}T00:00:00`).toLocaleDateString("pt-BR")
        : "data não informada";

      return `${responsavel} em ${dataLabel}`;
    },
    vendedorResponsavelOptions() {
      const vendedores = this.vendedoresAtribuicao.length
        ? this.vendedoresAtribuicao
        : this.usuarios;

      return vendedores.map((usuario) => ({
        value: usuario.id_usuario,
        text: usuario.nome || usuario.usuario,
      }));
    },
    desenvolvedorResponsavelOptions() {
      const desenvolvedores = this.desenvolvedoresAtribuicao.length
        ? this.desenvolvedoresAtribuicao
        : this.usuarios;

      return desenvolvedores.map((usuario) => ({
        value: usuario.id_usuario,
        text: usuario.nome || usuario.usuario,
      }));
    },
    treinamentoResponsavelOptions() {
      const usuarios = this.treinamentoAtribuicao.length
        ? this.treinamentoAtribuicao
        : this.usuarios;

      return usuarios.map((usuario) => ({
        value: usuario.id_usuario,
        text: usuario.nome || usuario.usuario,
      }));
    },
    treinamentoClienteFinalResponsavelOptions() {
      const usuarios = this.treinamentoClienteFinalAtribuicao.length
        ? this.treinamentoClienteFinalAtribuicao
        : this.usuarios;

      return usuarios.map((usuario) => ({
        value: usuario.id_usuario,
        text: usuario.nome || usuario.usuario,
      }));
    },
    websiteResponsavelOptions() {
      const usuarios = this.websiteAtribuicao.length
        ? this.websiteAtribuicao
        : this.usuarios;

      return usuarios.map((usuario) => ({
        value: usuario.id_usuario,
        text: usuario.nome || usuario.usuario,
      }));
    },
    contratoResponsavelWhiteLabelLabel() {
      return (
        this.optionText(
          this.desenvolvedorResponsavelOptions,
          this.contratoResponsaveisForm.id_usuario_white_label
        ) || "Selecione"
      );
    },
    contratoTreinamentoSelecionadoLabel() {
      return (
        this.optionText(
          this.treinamentoResponsavelOptions,
          this.contratoResponsaveisForm.id_usuario_treinamento
        ) || "Selecione"
      );
    },
    contratoTreinamentoClienteFinalSelecionadoLabel() {
      return (
        this.optionText(
          this.treinamentoClienteFinalResponsavelOptions,
          this.contratoResponsaveisForm.id_usuario_treinamento_cliente_final
        ) || "Selecione"
      );
    },
    contratoWebsiteSelecionadoLabel() {
      return (
        this.optionText(
          this.websiteResponsavelOptions,
          this.contratoResponsaveisForm.id_usuario_website
        ) || "Selecione"
      );
    },
    contratoExigeTreinamento() {
      return this.contratoTemBeneficioTreinamento();
    },
    contratoExigeTreinamentoClienteFinal() {
      return this.contratoTemBeneficioTreinamentoClienteFinal();
    },
    contratoExigeImplementacaoWhiteLabel() {
      if (
        this.oportunidade &&
        this.oportunidade.exige_implementacao_white_label_pos_contrato !==
          undefined
      ) {
        return this.booleanFlag(
          this.oportunidade.exige_implementacao_white_label_pos_contrato
        );
      }

      return this.beneficiosEstruturadosOportunidade().includes(
        "implementacao_white_label"
      );
    },
    contratoExigeWebsite() {
      return this.contratoTemBeneficioWebsite();
    },
    etapaDestinoSelecionada() {
      return this.findEtapaById(this.idEtapaDestino);
    },
    camposObrigatoriosMovimento() {
      return this.camposObrigatoriosEtapa(this.etapaDestinoSelecionada);
    },
    movimentoExigeValorNegociacao() {
      return this.camposObrigatoriosMovimento.some(
        (regra) => this.campoObrigatorioChave(regra) === "valor_negociacao"
      );
    },
    movimentoExigeTecnico() {
      return (
        !this.oportunidadeTemResponsavelTecnico(this.oportunidade) &&
        this.isEtapaApresentacaoTecnica(this.etapaDestinoSelecionada)
      );
    },
    movimentoExigeAtribuicao() {
      return this.movimentoExigeAtribuicaoParaDestino(
        this.etapaDestinoSelecionada
      );
    },
    funilAtualUsuariosAcesso() {
      if (!this.oportunidade) {
        return [];
      }

      const funil = this.funis.find(
        (item) => item.id_crm_funil === this.oportunidade.id_crm_funil
      );
      const acessos =
        funil && funil.usuarios_acesso ? funil.usuarios_acesso : [];

      return acessos
        .filter((acesso) => acesso.ativo !== false)
        .map((acesso) => Number(acesso.id_usuario));
    },
    usuarioDestinoSemAcesso() {
      if (!this.movimentoExigeAtribuicao || !this.idUsuarioDestino) {
        return false;
      }

      if (this.usuarioTemAcessoPorPapelAoFunilAtual(this.idUsuarioDestino)) {
        return false;
      }

      const ids = this.funilAtualUsuariosAcesso;
      return ids.length > 0 && !ids.includes(Number(this.idUsuarioDestino));
    },
    motivoPerdaOptions() {
      return [{ value: null, text: "Selecione" }].concat(
        this.motivosPerda.map((motivo) => ({
          value: motivo.id_crm_motivo_perda,
          text: motivo.nome,
        }))
      );
    },
    agendaSlots() {
      return this.agendaHorarios ? this.agendaHorarios.slots || [] : [];
    },
    tipoTarefaOptions() {
      const ativos = this.tiposTarefa.filter((tipo) => tipo.ativo !== false);
      const tipos = ativos.length
        ? ativos
        : [
            { nome: "Tarefa", chave: "tarefa" },
            { nome: "Ligação", chave: "ligacao" },
            { nome: "Reunião", chave: "reuniao" },
            { nome: "Nota", chave: "nota" },
            { nome: "Follow-up", chave: "follow_up" },
          ];

      return tipos.map((tipo) => ({
        value: tipo.chave,
        text: tipo.nome,
      }));
    },
    agendaClienteLabel() {
      return this.oportunidade
        ? this.oportunidade.pessoa ||
            this.oportunidade.contato ||
            "Sem cliente/contato informado"
        : "-";
    },
    oportunidadeSubtitle() {
      if (!this.oportunidade) {
        return "";
      }

      return [
        this.oportunidade.contato || this.oportunidade.pessoa,
        this.oportunidade.funil?.nome || "Funil não informado",
        this.oportunidade.etapa,
      ]
        .filter(Boolean)
        .join(" · ");
    },
  },
  mounted() {
    this.loadBase();
  },
  watch: {
    idOportunidade(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.loadBase();
      }
    },
    "agendaForm.id_usuario": "resetAgendaHorarios",
    "agendaForm.data_inicio": "resetAgendaHorarios",
    "agendaForm.duracao_minutos": "resetAgendaHorarios",
    "agendaReagendamentoForm.data_inicio": "resetAgendaReagendamentoHorarios",
    "agendaReagendamentoForm.duracao_minutos":
      "resetAgendaReagendamentoHorarios",
  },
  methods: {
    emptyTarefaForm() {
      return {
        titulo: "",
        tipo: "tarefa",
        id_usuario: null,
        vencimento_em: this.toDateTimeLocal(new Date()),
        descricao: "",
        concluir_ao_criar: false,
      };
    },
    emptyAgendaForm() {
      return {
        id_crm_atividade: null,
        titulo: "",
        tipo: "follow_up",
        id_usuario: null,
        data_inicio: this.toDateTimeLocal(new Date()),
        duracao_minutos: 30,
        descricao: "",
      };
    },
    emptyAgendaReagendamentoForm() {
      return {
        data_inicio: "",
        duracao_minutos: 30,
        motivo_reagendamento: "",
      };
    },
    emptyLinkSolicitacaoForm() {
      const periodo = this.periodoLinkSolicitacaoTecnica();

      return {
        data_inicio: periodo.data_inicio,
        data_fim: periodo.data_fim,
        duracao_minutos: 30,
        observacoes: "",
      };
    },
    emptyContratoResponsaveisForm() {
      return {
        id_usuario_white_label: null,
        id_usuario_treinamento: null,
        id_usuario_treinamento_cliente_final: null,
        id_usuario_website: null,
        exige_treinamento: false,
        exige_treinamento_cliente_final: false,
        exige_website: false,
      };
    },
    emptyPerdaForm() {
      return {
        id_crm_motivo_perda: null,
        motivo_perda_observacao: "",
      };
    },
    emptyGanhoForm() {
      return {
        titulo: "",
        cliente: "",
        nome_correto: "",
        cnpj: "",
        email_cliente: "",
        telefone_cliente: "",
        endereco_cliente: "",
        cep: "",
        logradouro: "",
        numero: "",
        bairro: "",
        complemento: "",
        id_cidade: null,
        cidade: "",
        uf: "",
        valor_final: 0,
        beneficios: "",
        beneficios_livres: "",
        beneficios_estruturados: [],
        cliente_final: false,
      };
    },
    emptyDadosForm() {
      return {
        titulo: "",
        id_crm_funil: null,
        id_crm_etapa: null,
        id_usuario: null,
        id_crm_fonte: null,
        campanha: "",
        temperatura: null,
        valor_estimado: 0,
        previsao_fechamento: null,
        proxima_atividade_em: null,
        campos_comerciais: {},
      };
    },
    async loadBase() {
      this.loading = true;
      this.erroOportunidade = "";

      try {
        await this.loadOportunidade();
      } catch (error) {
        this.erroOportunidade = this.erroMensagem(
          error,
          "Não foi possível abrir este card."
        );
        this.$toast.error(this.erroOportunidade);
        this.loading = false;
        return;
      }

      this.loading = false;

      const resultados = await Promise.allSettled([
        this.loadTimeline(true),
        this.loadAuxiliares(),
        this.loadRelacionados(),
      ]);
      const falha = resultados.find(
        (resultado) => resultado.status === "rejected"
      );

      if (falha) {
        this.$toast.error(
          this.erroMensagem(
            falha.reason,
            "Algumas informações do card não puderam ser carregadas."
          )
        );
      }
    },
    async loadAuxiliares() {
      const [
        motivos,
        usuarios,
        tiposTarefa,
        funis,
        fontes,
        temperaturas,
        vendedoresAtribuicao,
        desenvolvedoresAtribuicao,
        treinamentoAtribuicao,
        treinamentoClienteFinalAtribuicao,
        websiteAtribuicao,
      ] = (
        await Promise.allSettled([
          crmService.motivosPerda(),
          crmService.usuarios({
            page: 1,
            per_page: 100,
            sort: [{ field: "nome", type: "asc" }],
          }),
          crmService.tiposTarefaConfiguracao(),
          crmService.funis(),
          crmService.fontes(),
          crmService.temperaturasConfiguracao(),
          crmService.usuariosAtribuicoesConfiguracao({ papel: "vendedor" }),
          crmService.usuariosAtribuicoesConfiguracao({
            papel: "desenvolvedor",
          }),
          crmService.usuariosAtribuicoesConfiguracao({ papel: "treinamento" }),
          crmService.usuariosAtribuicoesConfiguracao({
            papel: "treinamento_cliente_final",
          }),
          crmService.usuariosAtribuicoesConfiguracao({ papel: "website" }),
        ])
      ).map((resultado) =>
        resultado.status === "fulfilled" ? resultado.value : null
      );
      this.motivosPerda = this.normalizeCollection(
        motivos ? motivos.data : null
      );
      this.usuarios = this.normalizeCollection(usuarios ? usuarios.data : null);
      this.tiposTarefa = this.normalizeCollection(
        tiposTarefa ? tiposTarefa.data : null
      );
      this.funis = this.normalizeCollection(funis ? funis.data : null);
      this.fontes = this.normalizeCollection(fontes ? fontes.data : null);
      this.temperaturaOptions = this.temperaturaOptionsFromList(
        this.normalizeCollection(temperaturas ? temperaturas.data : null)
      );
      this.vendedoresAtribuicao =
        this.vendedoresFromAtribuicoes(vendedoresAtribuicao);
      this.desenvolvedoresAtribuicao = this.desenvolvedoresFromAtribuicoes(
        desenvolvedoresAtribuicao
      );
      this.treinamentoAtribuicao = this.usuariosPorPapelFromAtribuicoes(
        treinamentoAtribuicao,
        "treinamento"
      );
      this.treinamentoClienteFinalAtribuicao =
        this.usuariosPorPapelFromAtribuicoes(
          treinamentoClienteFinalAtribuicao,
          "treinamento_cliente_final"
        );
      this.websiteAtribuicao = this.usuariosPorPapelFromAtribuicoes(
        websiteAtribuicao,
        "website"
      );
    },
    async loadOportunidade() {
      const { data } = await crmService.detalheOportunidade({
        id_crm_oportunidade: this.idOportunidade,
        origem: this.$route.query.origem || null,
      });
      const oportunidade =
        data && data.status === true && data.data ? data.data : data;

      if (data && data.status === false) {
        throw new Error(
          data.msg || data.message || "Oportunidade não encontrada."
        );
      }

      if (!oportunidade || !oportunidade.id_crm_oportunidade) {
        throw new Error("Oportunidade não encontrada.");
      }

      this.oportunidade = oportunidade;
    },
    async loadRelacionados() {
      this.listasLoading = true;

      try {
        const resultados = await Promise.allSettled([
          this.loadAtividades(),
          this.loadAgendas(),
          this.loadSolicitacoesTecnicas(),
        ]);
        const falha = resultados.find(
          (resultado) => resultado.status === "rejected"
        );

        if (falha) {
          this.$toast.error(
            this.erroMensagem(
              falha.reason,
              "Algumas listas do card não puderam ser carregadas."
            )
          );
        }
      } finally {
        this.listasLoading = false;
      }
    },
    async loadAtividades() {
      const { data } = await crmService.atividades({
        page: 1,
        per_page: 100,
        consolidar_fluxo: true,
        columnFilters: {
          id_crm_oportunidade: this.idOportunidade,
        },
        sort: [{ field: "vencimento_em", type: "asc" }],
      });
      this.atividades = this.normalizeCollection(data);
    },
    async loadAgendas() {
      const periodo = this.periodoAgendaDetalhe();
      const { data } = await crmService.agenda({
        ...periodo,
        id_crm_oportunidade: this.idOportunidade,
        consolidar_fluxo: true,
        page: 1,
        per_page: 100,
        sort: [{ field: "data_inicio", type: "asc" }],
      });
      this.agendas = this.normalizeCollection(data);
    },
    async loadSolicitacoesTecnicas() {
      const { data } = await crmService.solicitacoesTecnicasAgenda({
        id_crm_oportunidade: this.idOportunidade,
        page: 1,
        per_page: 20,
        sort: [{ field: "created_at", type: "desc" }],
      });
      this.solicitacoesTecnicas = this.normalizeCollection(data);
    },
    async loadTimeline(reset) {
      this.timelineLoading = reset;
      const offset = reset ? 0 : this.timeline.length;

      try {
        const { data } = await crmService.timeline({
          id_crm_oportunidade: this.idOportunidade,
          categoria: this.timelineFiltro,
          limit: TIMELINE_LIMIT,
          offset,
        });
        const itens = this.normalizeCollection(data);
        this.timeline = reset ? itens : this.timeline.concat(itens);
        this.timelineHasMore = itens.length === TIMELINE_LIMIT;
      } finally {
        this.timelineLoading = false;
      }
    },
    alterarFiltroTimeline(filtro) {
      if (this.timelineFiltro === filtro) {
        return;
      }

      this.timelineFiltro = filtro;
      this.loadTimeline(true);
    },
    async salvarNotaRapida() {
      const descricao = (this.timelineNotaRapida || "").trim();

      if (!descricao) {
        this.$toast.error("Informe a nota.");
        return;
      }

      await crmService.adicionarNota({
        id_crm_oportunidade: this.idOportunidade,
        id_crm_contato: this.oportunidade.id_crm_contato,
        descricao,
      });
      this.timelineNotaRapida = "";
      this.timelineFiltro = "notas";
      this.$toast.success("Nota adicionada ao histórico.");
      await Promise.all([
        this.loadTimeline(true),
        this.loadOportunidade(),
        this.loadRelacionados(),
      ]);
    },
    abrirUploadAnexoTimeline() {
      if (this.timelineUploading) {
        return;
      }

      this.$refs.timelineAnexoInput?.click();
    },
    async salvarAnexoTimeline(event) {
      const file = event.target.files && event.target.files[0];

      if (!file) {
        return;
      }

      const formData = new FormData();
      formData.append("id_crm_oportunidade", this.idOportunidade);
      if (this.oportunidade.id_crm_contato) {
        formData.append("id_crm_contato", this.oportunidade.id_crm_contato);
      }
      formData.append("arquivo", file);

      this.timelineUploading = true;

      try {
        await crmService.adicionarAnexo(formData);
        this.timelineFiltro = "anexos";
        this.$toast.success("Anexo adicionado ao histórico.");
        await this.loadTimeline(true);
      } finally {
        this.timelineUploading = false;
        event.target.value = "";
      }
    },
    async adicionarNota() {
      if (!this.nota) {
        this.$toast.error("Informe a nota.");
        return;
      }

      await crmService.adicionarNota({
        id_crm_oportunidade: this.idOportunidade,
        id_crm_contato: this.oportunidade.id_crm_contato,
        descricao: this.nota,
      });
      this.nota = "";
      this.$toast.success("Nota adicionada com sucesso.");
      this.afterAction();
    },
    abrirModalDados() {
      this.dadosForm = {
        titulo: this.oportunidade.titulo || "",
        id_crm_funil: this.oportunidade.id_crm_funil,
        id_crm_etapa: this.oportunidade.id_crm_etapa,
        id_usuario: this.oportunidade.id_usuario,
        id_crm_fonte: this.oportunidade.id_crm_fonte || null,
        campanha: this.oportunidade.campanha || "",
        temperatura: this.oportunidade.temperatura || null,
        valor_estimado: this.oportunidade.valor_estimado || 0,
        previsao_fechamento: this.toDateInput(
          this.oportunidade.previsao_fechamento
        ),
        proxima_atividade_em: this.toDateTimeLocal(
          this.oportunidade.proxima_atividade_em
        ),
        campos_comerciais: this.camposComerciaisFormValues(),
      };
      this.$bvModal.show("crm-detalhe-dados-modal");
    },
    onDadosFunilChange() {
      const etapas = this.etapaOptions(this.dadosForm.id_crm_funil);
      this.dadosForm.id_crm_etapa = etapas.length ? etapas[0].value : null;
    },
    async salvarDados() {
      const titulo = (this.dadosForm.titulo || "").trim();

      if (!titulo) {
        this.$toast.error("Informe o título da oportunidade.");
        return;
      }

      if (!this.dadosForm.id_crm_funil || !this.dadosForm.id_crm_etapa) {
        this.$toast.error("Selecione o funil e a etapa.");
        return;
      }

      await crmService.atualizarOportunidade({
        id_crm_oportunidade: this.idOportunidade,
        ...this.dadosForm,
        titulo,
        campanha: this.dadosForm.campanha || null,
        valor_estimado: Number(this.dadosForm.valor_estimado || 0),
        previsao_fechamento: this.dadosForm.previsao_fechamento || null,
        proxima_atividade_em: this.fromDateTimeLocal(
          this.dadosForm.proxima_atividade_em
        ),
        campos_comerciais: this.camposComerciaisPayload(),
      });
      this.$toast.success("Dados comerciais atualizados.");
      this.$bvModal.hide("crm-detalhe-dados-modal");
      this.afterAction();
    },
    abrirModalContato() {
      this.contatoSelecionado = null;
      this.$bvModal.show("crm-detalhe-contato-modal");
    },
    async criarContatoRapidoOportunidade(payload) {
      const contatoPayload =
        typeof payload === "string"
          ? { nome: payload, origem_criacao_rapida: true }
          : { ...payload, origem_criacao_rapida: true };
      const { data } = await crmService.criarContato(contatoPayload);
      const contato = data.contato || data;
      this.contatoSelecionado = {
        tipo: "contato",
        label: contato.nome,
        id_crm_contato: contato.id_crm_contato,
        id_pessoa: contato.id_pessoa || null,
        id_crm_oportunidade: null,
        raw: contato,
      };
      this.$toast.success("Contato criado e selecionado.");
    },
    async salvarContatoOportunidade() {
      if (!this.contatoSelecionado) {
        this.$toast.error("Selecione ou crie um contato.");
        return;
      }

      await crmService.atualizarOportunidade({
        id_crm_oportunidade: this.idOportunidade,
        id_crm_contato: this.contatoSelecionado.id_crm_contato,
        id_pessoa:
          this.contatoSelecionado.id_pessoa || this.oportunidade.id_pessoa,
      });
      this.$toast.success("Contato da oportunidade atualizado.");
      this.$bvModal.hide("crm-detalhe-contato-modal");
      this.afterAction();
    },
    abrirModalEmpresa() {
      this.empresaSelecionada = null;
      this.$bvModal.show("crm-detalhe-empresa-modal");
    },
    async salvarEmpresaOportunidade() {
      if (!this.empresaSelecionada || !this.empresaSelecionada.id_pessoa) {
        this.$toast.error("Selecione uma Pessoa existente.");
        return;
      }

      if (this.oportunidade.id_crm_contato) {
        await crmService.vincularPessoaContato({
          id_crm_contato: this.oportunidade.id_crm_contato,
          id_pessoa: this.empresaSelecionada.id_pessoa,
        });
      }

      await crmService.atualizarOportunidade({
        id_crm_oportunidade: this.idOportunidade,
        id_pessoa: this.empresaSelecionada.id_pessoa,
      });
      this.$toast.success("Empresa/pessoa associada à oportunidade.");
      this.$bvModal.hide("crm-detalhe-empresa-modal");
      this.afterAction();
    },
    async copiarTelefoneContato() {
      if (!this.contatoTelefone) {
        return;
      }

      try {
        if (this.$copyText) {
          await this.$copyText(this.contatoTelefone);
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.contatoTelefone);
        } else {
          this.copiarTelefoneFallback(this.contatoTelefone);
        }

        this.$toast.success("Telefone copiado.");
      } catch (error) {
        this.copiarTelefoneFallback(this.contatoTelefone);
        this.$toast.success("Telefone copiado.");
      }
    },
    copiarTelefoneFallback(telefone) {
      const input = document.createElement("textarea");
      input.value = telefone;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    },
    async copiarTexto(texto) {
      if (!texto) {
        return;
      }

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(texto);
        } else if (this.$copyText) {
          await this.$copyText(texto);
        } else {
          this.copiarTelefoneFallback(texto);
        }

        this.$toast.success("Link copiado.");
      } catch (error) {
        try {
          this.copiarTelefoneFallback(texto);
          this.$toast.success("Link copiado.");
        } catch (fallbackError) {
          this.$toast.error("Não foi possível copiar o link.");
        }
      }
    },
    abrirWhatsappContato() {
      if (!this.whatsappUrl) {
        return;
      }

      window.open(this.whatsappUrl, "_blank", "noopener");
    },
    abrirDadosNegociacao() {
      this.$bvModal.show("crm-detalhe-dados-negociacao-modal");
    },
    enviarContratoWhatsapp() {
      if (!this.whatsappUrl) {
        this.$toast.error("Telefone não informado para enviar o contrato.");
        return;
      }

      const telefone = this.normalizarTelefoneWhatsapp(this.contatoTelefone);
      const mensagem = `Olá ${this.contatoPrincipal}, segue o contrato referente à negociação ${this.oportunidade.titulo}.`;
      window.open(
        `https://web.whatsapp.com/send?phone=${telefone}&text=${encodeURIComponent(
          mensagem
        )}`,
        "_blank",
        "noopener"
      );
    },
    enviarContratoEmail() {
      if (!this.contatoEmail) {
        this.$toast.error("E-mail não informado para enviar o contrato.");
        return;
      }

      const assunto = `Contrato - ${this.contatoPrincipal}`;
      const corpo = `Olá,\n\nSegue o contrato referente à negociação ${
        this.oportunidade.titulo
      }.\n\nValor negociado: ${this.formatMoney(
        this.valorPrincipalDetalhe
      )}.\n`;
      window.location.href = `mailto:${
        this.contatoEmail
      }?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(
        corpo
      )}`;
    },
    isTarefaContrato(tarefa) {
      return tarefa && tarefa.tipo === "contrato_assinado";
    },
    contratoTemBeneficioWebsite() {
      if (
        this.oportunidade &&
        this.oportunidade.exige_website_pos_contrato !== undefined
      ) {
        return this.booleanFlag(this.oportunidade.exige_website_pos_contrato);
      }

      return this.beneficiosEstruturadosOportunidade().includes("website");
    },
    contratoTemBeneficioTreinamento() {
      if (
        this.oportunidade &&
        this.oportunidade.exige_treinamento_pos_contrato !== undefined
      ) {
        return this.booleanFlag(
          this.oportunidade.exige_treinamento_pos_contrato
        );
      }

      return false;
    },
    contratoTemBeneficioTreinamentoClienteFinal() {
      if (
        this.oportunidade &&
        this.oportunidade.exige_treinamento_cliente_final_pos_contrato !==
          undefined
      ) {
        return this.booleanFlag(
          this.oportunidade.exige_treinamento_cliente_final_pos_contrato
        );
      }

      return this.beneficiosEstruturadosOportunidade().includes(
        "treinamento_cliente_final"
      );
    },
    oportunidadeVendaClienteFinal() {
      return (
        this.oportunidade &&
        (this.oportunidade.tipo_fluxo === "vendas_cliente_final" ||
          this.oportunidade.funil_chave_preset === "vendas_cliente_final")
      );
    },
    async abrirModalResponsaveisContrato(tarefa) {
      const exigeTreinamento = this.contratoExigeTreinamento;
      const exigeTreinamentoClienteFinal =
        this.contratoExigeTreinamentoClienteFinal;
      const exigeImplementacao = this.contratoExigeImplementacaoWhiteLabel;
      const exigeWebsite = this.contratoExigeWebsite;

      await this.ensureContratoResponsaveisOptions({
        exige_treinamento: exigeTreinamento,
        exige_treinamento_cliente_final: exigeTreinamentoClienteFinal,
        exige_implementacao_white_label: exigeImplementacao,
        exige_website: exigeWebsite,
      });

      this.contratoResponsaveisTarefa = tarefa;
      this.contratoResponsaveisForm = {
        ...this.emptyContratoResponsaveisForm(),
        id_usuario_white_label: exigeImplementacao
          ? this.idResponsavelWhiteLabelPadrao()
          : null,
        id_usuario_treinamento_cliente_final: exigeTreinamentoClienteFinal
          ? this.idResponsavelTreinamentoClienteFinalPadrao()
          : null,
        exige_treinamento: exigeTreinamento,
        exige_treinamento_cliente_final: exigeTreinamentoClienteFinal,
        exige_implementacao_white_label: exigeImplementacao,
        exige_website: exigeWebsite,
      };
      this.$bvModal.show("crm-detalhe-responsaveis-contrato-modal");
    },
    async ensureContratoResponsaveisOptions(form = {}) {
      const precisaImplementacao =
        form.exige_implementacao_white_label !== false;
      const precisaTreinamento = !!form.exige_treinamento;
      const precisaTreinamentoClienteFinal =
        !!form.exige_treinamento_cliente_final;
      const precisaWebsite = !!form.exige_website;
      const temOpcoes =
        (!precisaImplementacao ||
          this.desenvolvedorResponsavelOptions.length > 0) &&
        (!precisaTreinamento ||
          this.treinamentoResponsavelOptions.length > 0) &&
        (!precisaTreinamentoClienteFinal ||
          this.treinamentoClienteFinalResponsavelOptions.length > 0) &&
        (!precisaWebsite || this.websiteResponsavelOptions.length > 0);

      if (temOpcoes) {
        return;
      }

      await this.loadAuxiliares();
    },
    idResponsavelTreinamentoClienteFinalPadrao() {
      const primeiro = this.treinamentoClienteFinalResponsavelOptions.find(
        (option) => option.value
      );

      return primeiro ? primeiro.value : null;
    },
    idResponsavelWhiteLabelPadrao() {
      if (!this.oportunidade) {
        return null;
      }

      const tecnico = Number(
        this.oportunidade.id_responsavel_tecnico ||
          this.valorCampoComercial(this.oportunidade, "id_responsavel_tecnico")
      );
      if (
        tecnico &&
        this.optionText(this.desenvolvedorResponsavelOptions, tecnico)
      ) {
        return tecnico;
      }

      const primeiroDesenvolvedor = this.desenvolvedorResponsavelOptions.find(
        (option) => option.value
      );

      return primeiroDesenvolvedor ? Number(primeiroDesenvolvedor.value) : null;
    },
    abrirModalTarefa() {
      this.tarefaEditando = null;
      this.tarefaForm = this.emptyTarefaForm();
      this.tarefaForm.id_usuario = this.oportunidade.id_usuario;
      this.tarefaForm.tipo = this.tipoTarefaOptions.length
        ? this.tipoTarefaOptions[0].value
        : "tarefa";
      this.tarefaDrawerVisible = true;
    },
    abrirEditarTarefa(tarefa) {
      if (tarefa.status !== "aberta") {
        this.$toast.warning("Tarefas concluídas não podem ser editadas.");
        return;
      }

      this.tarefaEditando = tarefa;
      this.tarefaForm = {
        titulo: tarefa.titulo || "",
        tipo: tarefa.tipo || "tarefa",
        id_usuario: tarefa.id_usuario || this.oportunidade.id_usuario,
        vencimento_em: tarefa.vencimento_em
          ? this.toDateTimeLocal(tarefa.vencimento_em)
          : this.toDateTimeLocal(new Date()),
        descricao: tarefa.descricao || "",
        concluir_ao_criar: false,
      };
      this.tarefaDrawerVisible = true;
    },
    async salvarTarefa(agendar = false) {
      if (!this.tarefaForm.titulo) {
        this.$toast.error("Informe o título da tarefa.");
        return;
      }

      let atividade = null;

      try {
        const payload = {
          ...this.tarefaForm,
          vencimento_em: this.fromDateTimeLocal(this.tarefaForm.vencimento_em),
        };
        const response = this.tarefaEditando
          ? await crmService.atualizarAtividade({
              ...payload,
              id_crm_atividade: this.tarefaEditando.id_crm_atividade,
            })
          : await crmService.criarAtividade({
              ...payload,
              id_crm_oportunidade: this.idOportunidade,
              id_crm_contato: this.oportunidade.id_crm_contato,
            });
        atividade = this.responseData(response);
        this.$toast.success(
          this.tarefaEditando
            ? "Tarefa atualizada com sucesso."
            : "Tarefa criada com sucesso."
        );
        this.tarefaDrawerVisible = false;
        this.tarefaEditando = null;
      } catch (error) {
        this.$toast.error(
          this.erroMensagem(error, "Não foi possível salvar a tarefa.")
        );
        return;
      }

      if (agendar) {
        this.abrirModalAgenda(atividade);
      }

      this.afterAction();
    },
    abrirModalAgenda(tarefa = null) {
      const treinamentoClienteFinal = this.isTreinamentoClienteFinal(
        this.oportunidade
      );
      this.agendaForm = this.emptyAgendaForm();
      this.agendaForm.id_usuario = treinamentoClienteFinal
        ? this.idPrimeiroResponsavelTreinamento()
        : this.oportunidade.id_usuario;
      this.agendaForm.titulo = treinamentoClienteFinal
        ? `Treinamento - ${this.oportunidade.titulo}`
        : `Agenda - ${this.oportunidade.titulo}`;
      this.agendaForm.tipo = treinamentoClienteFinal
        ? "treinamento_cliente_final"
        : this.agendaForm.tipo;
      this.agendaForm.descricao = treinamentoClienteFinal
        ? "Agendamento criado a partir do funil Treinamento Cliente Final."
        : this.agendaForm.descricao;
      this.tarefaAgendamentoOrigem = tarefa;
      this.agendaSolicitacaoOrigem = null;

      if (tarefa) {
        this.preencherAgendaPorTarefa(tarefa);
      }

      this.agendaHorarios = null;
      this.agendaHorariosErro = "";
      this.agendaDrawerVisible = true;
    },
    preencherAgendaPorTarefa(tarefa) {
      const treinamentoClienteFinal = this.isTreinamentoClienteFinal(
        this.oportunidade
      );
      const agendarTreinamento = tarefa.tipo === "agendar_treinamento";
      this.agendaForm.id_crm_atividade = tarefa.id_crm_atividade || null;
      this.agendaForm.titulo =
        treinamentoClienteFinal || agendarTreinamento
          ? `Treinamento - ${this.oportunidade.titulo}`
          : `Tarefa - ${tarefa.titulo}`;
      this.agendaForm.id_usuario =
        treinamentoClienteFinal || agendarTreinamento
          ? this.agendaForm.id_usuario ||
            this.idPrimeiroResponsavelTreinamento() ||
            tarefa.id_usuario ||
            this.oportunidade.id_usuario
          : tarefa.id_usuario || this.oportunidade.id_usuario;
      this.agendaForm.tipo = treinamentoClienteFinal
        ? "treinamento_cliente_final"
        : agendarTreinamento
        ? "treinamento"
        : this.agendaForm.tipo;
      this.agendaForm.data_inicio = tarefa.vencimento_em
        ? this.toDateTimeLocal(tarefa.vencimento_em)
        : this.agendaForm.data_inicio;
      this.agendaForm.descricao = [
        `Tarefa: ${tarefa.titulo}`,
        `Tipo: ${this.tipoTarefaLabel(tarefa.tipo)}`,
        tarefa.descricao ? `Descrição: ${tarefa.descricao}` : null,
      ]
        .filter(Boolean)
        .join("\n");
    },
    abrirAgendaSolicitacaoTecnica(solicitacao, buscarHorarios = false) {
      this.agendaForm = this.emptyAgendaForm();
      this.agendaSolicitacaoOrigem = solicitacao;
      this.tarefaAgendamentoOrigem = {
        id_crm_atividade: solicitacao.id_crm_atividade,
        titulo: "Apresentação técnica",
      };
      this.agendaForm.id_crm_atividade = solicitacao.id_crm_atividade || null;
      this.agendaForm.id_usuario =
        solicitacao.id_usuario_tecnico || this.oportunidade.id_usuario;
      this.agendaForm.titulo = `Apresentação técnica - ${this.oportunidade.titulo}`;
      this.agendaForm.tipo = "apresentacao_tecnica";
      this.agendaForm.duracao_minutos = solicitacao.link_duracao_minutos || 30;
      this.agendaForm.descricao = [
        "Solicitação de apresentação técnica do CRM.",
        solicitacao.observacoes
          ? `Observações: ${solicitacao.observacoes}`
          : null,
      ]
        .filter(Boolean)
        .join("\n");
      this.agendaHorarios = null;
      this.agendaHorariosErro = "";
      this.agendaDrawerVisible = true;

      if (buscarHorarios) {
        this.$nextTick(() => this.buscarHorariosAgenda());
      }
    },
    async buscarHorariosAgenda() {
      if (!this.agendaForm.id_usuario) {
        this.$toast.error("Selecione o responsável.");
        return;
      }

      this.loadingAgendaHorarios = true;
      this.agendaHorariosErro = "";

      try {
        const { data } = await crmService.horariosDisponiveisAgenda({
          data: this.dateFromDateTimeLocal(this.agendaForm.data_inicio),
          id_usuario: this.agendaForm.id_usuario,
          id_crm_oportunidade: this.idOportunidade,
          id_crm_atividade: this.agendaForm.id_crm_atividade,
          duracao_minutos: Number(this.agendaForm.duracao_minutos || 30),
        });
        this.agendaHorarios = data;
      } catch (error) {
        this.agendaHorarios = null;
        this.agendaHorariosErro = this.apiError(
          error,
          "Não foi possível buscar os horários."
        );
      } finally {
        this.loadingAgendaHorarios = false;
      }
    },
    resetAgendaHorarios() {
      if (!this.agendaDrawerVisible) {
        return;
      }

      this.agendaHorarios = null;
      this.agendaHorariosErro = "";
    },
    selecionarHorarioAgenda(slot) {
      if (!slot.disponivel) {
        return;
      }

      this.agendaForm.data_inicio = this.toDateTimeLocal(slot.inicio);
    },
    agendaSlotSelecionado(slot) {
      return this.agendaForm.data_inicio === this.toDateTimeLocal(slot.inicio);
    },
    agendaReagendamentoResponsavelId() {
      return (
        this.agendaReagendamento?.id_usuario ||
        this.agendaReagendamento?.agenda_ativa_id_usuario ||
        this.oportunidade?.id_usuario ||
        null
      );
    },
    agendaReagendamentoAtividadeId() {
      return (
        this.agendaReagendamento?.id_crm_atividade ||
        this.agendaReagendamento?.agenda_ativa_id_crm_atividade ||
        null
      );
    },
    async buscarHorariosReagendamentoAgenda() {
      const idUsuario = this.agendaReagendamentoResponsavelId();

      if (!idUsuario) {
        this.$toast.error("Não encontrei o responsável deste agendamento.");
        return;
      }

      this.loadingAgendaReagendamentoHorarios = true;
      this.agendaReagendamentoHorariosErro = "";

      try {
        const { data } = await crmService.horariosDisponiveisAgenda({
          data: this.dateFromDateTimeLocal(
            this.agendaReagendamentoForm.data_inicio
          ),
          id_usuario: idUsuario,
          id_crm_oportunidade: this.idOportunidade,
          id_crm_atividade: this.agendaReagendamentoAtividadeId(),
          id_crm_agenda: this.agendaReagendamento?.id_crm_agenda || null,
          duracao_minutos: Number(
            this.agendaReagendamentoForm.duracao_minutos || 30
          ),
        });
        this.agendaReagendamentoHorarios = data;
      } catch (error) {
        this.agendaReagendamentoHorarios = null;
        this.agendaReagendamentoHorariosErro = this.apiError(
          error,
          "Não foi possível buscar os horários."
        );
      } finally {
        this.loadingAgendaReagendamentoHorarios = false;
      }
    },
    resetAgendaReagendamentoHorarios() {
      if (
        !this.agendaReagendamento ||
        this.agendaReagendamentoSelecionandoSlot
      ) {
        return;
      }

      this.agendaReagendamentoHorarios = null;
      this.agendaReagendamentoHorariosErro = "";
    },
    selecionarHorarioReagendamentoAgenda(slot) {
      if (!slot.disponivel) {
        return;
      }

      this.agendaReagendamentoSelecionandoSlot = true;
      this.agendaReagendamentoForm.data_inicio = this.toDateTimeLocal(
        slot.inicio
      );
      this.$nextTick(() => {
        this.agendaReagendamentoSelecionandoSlot = false;
      });
    },
    agendaReagendamentoSlotSelecionado(slot) {
      return (
        this.agendaReagendamentoForm.data_inicio ===
        this.toDateTimeLocal(slot.inicio)
      );
    },
    async criarAgenda() {
      if (!this.agendaForm.titulo || !this.agendaForm.data_inicio) {
        this.$toast.error("Informe o título e a data do agendamento.");
        return;
      }

      if (this.agendaSolicitacaoOrigem) {
        await crmService.agendarSolicitacaoTecnica({
          id_crm_solicitacao_tecnica:
            this.agendaSolicitacaoOrigem.id_crm_solicitacao_tecnica,
          data_inicio: this.fromDateTimeLocal(this.agendaForm.data_inicio),
          duracao_minutos: Number(this.agendaForm.duracao_minutos || 30),
          observacoes: this.agendaForm.descricao,
        });
        this.$toast.success("Apresentação técnica agendada com sucesso.");
        this.agendaDrawerVisible = false;
        this.tarefaAgendamentoOrigem = null;
        this.agendaSolicitacaoOrigem = null;
        this.afterAction();
        return;
      }

      await crmService.criarAgendamento({
        ...this.agendaForm,
        id_crm_oportunidade: this.idOportunidade,
        id_crm_contato: this.oportunidade.id_crm_contato,
        id_crm_atividade: this.agendaForm.id_crm_atividade,
        observacoes: this.tarefaAgendamentoOrigem
          ? `Agendamento criado a partir da tarefa #${this.tarefaAgendamentoOrigem.id_crm_atividade}: ${this.tarefaAgendamentoOrigem.titulo}`
          : this.agendaForm.descricao,
        data_inicio: this.fromDateTimeLocal(this.agendaForm.data_inicio),
      });
      this.$toast.success(
        this.isTreinamentoClienteFinal(this.oportunidade)
          ? "Treinamento agendado e enviado ao funil Treinamento."
          : this.tarefaAgendamentoOrigem &&
            this.tarefaAgendamentoOrigem.tipo === "agendar_treinamento"
          ? "Treinamento agendado com sucesso."
          : "Agendamento CRM criado com sucesso."
      );
      this.agendaDrawerVisible = false;
      this.tarefaAgendamentoOrigem = null;
      this.agendaSolicitacaoOrigem = null;
      this.afterAction();
    },
    abrirReagendamentoAgenda(agenda) {
      this.agendaReagendamento = agenda;
      this.agendaReagendamentoForm = {
        data_inicio: this.toDateTimeLocal(agenda.data_inicio),
        duracao_minutos: Number(agenda.duracao_minutos || 30),
        motivo_reagendamento: "",
      };
      this.agendaReagendamentoHorarios = null;
      this.agendaReagendamentoHorariosErro = "";
      this.$bvModal.show("crm-detalhe-reagendar-agenda-modal");
      this.$nextTick(() => this.buscarHorariosReagendamentoAgenda());
    },
    abrirReagendamentoTarefa(tarefa) {
      const agenda = this.agendaAtivaDaTarefa(tarefa);

      if (!agenda) {
        this.$toast.warning("Não encontrei o agendamento ativo desta tarefa.");
        return;
      }

      this.abrirReagendamentoAgenda(agenda);
    },
    async confirmarReagendamentoAgenda(event) {
      event.preventDefault();

      const agenda = this.agendaReagendamento;
      const motivo = String(
        this.agendaReagendamentoForm.motivo_reagendamento || ""
      ).trim();
      const duracao = Number(this.agendaReagendamentoForm.duracao_minutos || 0);

      if (!agenda || !this.agendaReagendamentoForm.data_inicio) {
        this.$toast.warning("Informe a nova data do agendamento.");
        return;
      }

      if (!Number.isInteger(duracao) || duracao < 1 || duracao > 1440) {
        this.$toast.warning("Informe uma duração entre 1 minuto e 24 horas.");
        return;
      }

      if (motivo.length < 3) {
        this.$toast.warning("Informe o motivo do reagendamento.");
        return;
      }

      await crmService.reagendarAgendamento({
        id_crm_agenda: agenda.id_crm_agenda,
        data_inicio: this.fromDateTimeLocal(
          this.agendaReagendamentoForm.data_inicio
        ),
        duracao_minutos: duracao,
        motivo_reagendamento: motivo,
      });

      this.$bvModal.hide("crm-detalhe-reagendar-agenda-modal");
      this.$toast.success("Agendamento reagendado.");
      await this.afterAction();
    },
    async cancelarAgenda(agenda) {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Cancelar agendamento",
        input: "textarea",
        inputLabel: "Motivo do cancelamento",
        inputPlaceholder: "Descreva o motivo",
        inputAttributes: {
          maxlength: 500,
        },
        showCancelButton: true,
        confirmButtonText: "Cancelar agendamento",
        cancelButtonText: "Voltar",
        preConfirm: (motivo) => {
          if (!motivo || motivo.trim().length < 3) {
            this.$swal.showValidationMessage(
              "Informe um motivo com pelo menos 3 caracteres."
            );
            return false;
          }

          return motivo.trim();
        },
      });

      if (!result.isConfirmed) return;

      await crmService.cancelarAgendamento({
        id_crm_agenda: agenda.id_crm_agenda,
        motivo_cancelamento: result.value,
      });
      this.$toast.success("Agendamento cancelado.");
      await this.afterAction();
    },
    async alterarStatusAgenda(agenda, status) {
      const result = await this.$swal.fire({
        icon: status === "cancelado" ? "warning" : "info",
        title: `${this.agendaStatusLabel(status)} este agendamento?`,
        showCancelButton: true,
        confirmButtonText: "Sim",
        cancelButtonText: "Não",
      });

      if (!result.isConfirmed) return;

      await crmService.alterarStatusAgenda({
        id_crm_agenda: agenda.id_crm_agenda,
        status,
      });
      this.$toast.success("Agendamento atualizado.");
      await this.afterAction();
    },
    abrirModalLinkSolicitacaoTecnica(solicitacao) {
      const periodo = this.periodoLinkSolicitacaoTecnica();

      this.linkSolicitacaoOrigem = solicitacao;
      this.linkSolicitacaoGerado = this.linkPublicoSolicitacao(solicitacao);
      this.linkSolicitacaoForm = {
        data_inicio: solicitacao.link_data_inicio
          ? this.toDateInput(solicitacao.link_data_inicio)
          : periodo.data_inicio,
        data_fim: solicitacao.link_data_fim
          ? this.toDateInput(solicitacao.link_data_fim)
          : periodo.data_fim,
        duracao_minutos: solicitacao.link_duracao_minutos || 30,
        observacoes: solicitacao.observacoes || "",
      };
      this.$bvModal.show("crm-detalhe-link-solicitacao-modal");
    },
    async confirmarGerarLinkSolicitacaoTecnica() {
      if (!this.linkSolicitacaoOrigem) {
        return;
      }

      const duracao = Number(this.linkSolicitacaoForm.duracao_minutos || 0);

      if (
        !this.linkSolicitacaoForm.data_inicio ||
        !this.linkSolicitacaoForm.data_fim
      ) {
        this.$toast.error("Informe o período do link.");
        return;
      }

      if (
        this.linkSolicitacaoForm.data_inicio > this.linkSolicitacaoForm.data_fim
      ) {
        this.$toast.error(
          "A data final deve ser igual ou posterior à inicial."
        );
        return;
      }

      if (duracao < 1 || duracao > 1440) {
        this.$toast.error("Informe uma duração entre 1 minuto e 24 horas.");
        return;
      }

      this.linkSolicitacaoLoading = true;

      try {
        const { data } = await crmService.gerarLinkSolicitacaoTecnica({
          id_crm_solicitacao_tecnica:
            this.linkSolicitacaoOrigem.id_crm_solicitacao_tecnica,
          data_inicio: this.linkSolicitacaoForm.data_inicio,
          data_fim: this.linkSolicitacaoForm.data_fim,
          duracao_minutos: duracao,
          observacoes: this.linkSolicitacaoForm.observacoes,
        });
        const solicitacao = this.responseData(data);
        this.linkSolicitacaoOrigem = solicitacao || this.linkSolicitacaoOrigem;
        this.linkSolicitacaoGerado = this.linkPublicoSolicitacao(
          solicitacao || this.linkSolicitacaoOrigem
        );
        this.$toast.success("Link de horários gerado.");
        await this.afterAction();
      } catch (error) {
        this.$toast.error(
          this.apiError(error, "Não foi possível gerar o link.")
        );
      } finally {
        this.linkSolicitacaoLoading = false;
      }
    },
    linkPublicoSolicitacao(solicitacao) {
      if (!solicitacao) {
        return "";
      }

      const link =
        solicitacao.link_url ||
        solicitacao.url_publica ||
        (solicitacao.data ? solicitacao.data.url_publica : "");
      const token = solicitacao.link_token || solicitacao.token || "";

      return this.normalizarLinkPublicoAgenda(link, token);
    },
    normalizarLinkPublicoAgenda(link, token = "") {
      const texto = String(link || "").trim();
      const tokenLink = token || this.tokenLinkAgendaLegado(texto);

      if (!texto && tokenLink) {
        return `${window.location.origin}/#/agendamentos/publico/${tokenLink}`;
      }

      if (!texto.includes("/#/crm/agenda")) {
        return texto;
      }

      return tokenLink
        ? `${window.location.origin}/#/agendamentos/publico/${tokenLink}`
        : texto;
    },
    tokenLinkAgendaLegado(link) {
      const match = String(link || "").match(/[?&]solicitacao_tecnica=([^&]+)/);

      return match ? decodeURIComponent(match[1]) : "";
    },
    async copiarLinkSolicitacaoTecnica(solicitacao) {
      const link = this.linkPublicoSolicitacao(solicitacao);

      if (!link) {
        return;
      }

      await this.copiarTexto(link);
    },
    async copiarLinkSolicitacaoModal() {
      if (!this.linkSolicitacaoGerado) {
        return;
      }

      await this.copiarTexto(this.linkSolicitacaoGerado);
    },
    enviarLinkTecnicoWhatsapp(solicitacao) {
      const link = this.linkPublicoSolicitacao(solicitacao);

      if (!link || !this.contatoTelefone) {
        return;
      }

      const telefone = this.normalizarTelefoneWhatsapp(this.contatoTelefone);
      const mensagem = `Olá ${this.contatoPrincipal}, escolha um horário para sua apresentação técnica: ${link}`;
      window.open(
        `https://web.whatsapp.com/send?phone=${telefone}&text=${encodeURIComponent(
          mensagem
        )}`,
        "_blank",
        "noopener"
      );
    },
    enviarLinkSolicitacaoModalWhatsapp() {
      this.enviarLinkTecnicoWhatsapp({
        link_url: this.linkSolicitacaoGerado,
      });
    },
    enviarLinkTecnicoEmail(solicitacao) {
      const link = this.linkPublicoSolicitacao(solicitacao);

      if (!link || !this.contatoEmail) {
        return;
      }

      const assunto = encodeURIComponent("Horários para apresentação técnica");
      const corpo = encodeURIComponent(
        `Olá ${this.contatoPrincipal},\n\nEscolha um horário para sua apresentação técnica pelo link:\n${link}`
      );
      window.open(
        `mailto:${this.contatoEmail}?subject=${assunto}&body=${corpo}`,
        "_blank",
        "noopener"
      );
    },
    enviarLinkSolicitacaoModalEmail() {
      this.enviarLinkTecnicoEmail({
        link_url: this.linkSolicitacaoGerado,
      });
    },
    async concluirTarefa(tarefa) {
      if (this.isTarefaContrato(tarefa)) {
        await this.loadOportunidade();
        await this.abrirModalResponsaveisContrato(tarefa);
        return;
      }

      await this.concluirTarefaComPayload(tarefa);
    },
    async concluirTarefaComPayload(tarefa, payload = {}) {
      await crmService.concluirAtividade({
        id_crm_atividade: tarefa.id_crm_atividade,
        ...payload,
      });
      this.$toast.success("Tarefa concluída.");
      await this.afterAction();
    },
    async confirmarConclusaoContrato() {
      if (!this.contratoResponsaveisTarefa) {
        return;
      }

      if (
        this.contratoExigeImplementacaoWhiteLabel &&
        !this.contratoResponsaveisForm.id_usuario_white_label
      ) {
        this.$toast.error(
          "Selecione o responsável pela implementação White Label."
        );
        return;
      }

      if (
        this.contratoExigeTreinamento &&
        !this.contratoResponsaveisForm.id_usuario_treinamento
      ) {
        this.$toast.error("Selecione o responsável pelo treinamento.");
        return;
      }

      if (
        this.contratoExigeTreinamentoClienteFinal &&
        !this.contratoResponsaveisForm.id_usuario_treinamento_cliente_final
      ) {
        this.$toast.error(
          "Selecione o responsável pelo Treinamento Cliente Final."
        );
        return;
      }

      if (
        this.contratoExigeWebsite &&
        !this.contratoResponsaveisForm.id_usuario_website
      ) {
        this.$toast.error("Selecione o responsável pelo website.");
        return;
      }

      this.contratoResponsaveisLoading = true;

      try {
        await this.concluirTarefaComPayload(this.contratoResponsaveisTarefa, {
          id_usuario_white_label: this.contratoExigeImplementacaoWhiteLabel
            ? this.contratoResponsaveisForm.id_usuario_white_label
            : null,
          id_usuario_treinamento: this.contratoExigeTreinamento
            ? this.contratoResponsaveisForm.id_usuario_treinamento
            : null,
          id_usuario_treinamento_cliente_final: this
            .contratoExigeTreinamentoClienteFinal
            ? this.contratoResponsaveisForm.id_usuario_treinamento_cliente_final
            : null,
          id_usuario_website: this.contratoExigeWebsite
            ? this.contratoResponsaveisForm.id_usuario_website
            : null,
        });
        this.$bvModal.hide("crm-detalhe-responsaveis-contrato-modal");
        this.contratoResponsaveisTarefa = null;
        this.contratoResponsaveisForm = this.emptyContratoResponsaveisForm();
      } finally {
        this.contratoResponsaveisLoading = false;
      }
    },
    abrirModalGanho() {
      const clienteFinal = this.oportunidadeVendaClienteFinal();
      const enderecoPessoa = this.enderecoPessoaOportunidade(this.oportunidade);
      this.ganhoForm = {
        ...this.emptyGanhoForm(),
        titulo: this.oportunidade.titulo || "",
        cliente: this.contatoPrincipal,
        nome_correto: this.nomeClienteFechamento,
        cnpj: this.documentoClienteFechamento,
        email_cliente: this.oportunidade.contato_email || "",
        telefone_cliente: this.contatoTelefone || "",
        endereco_cliente:
          this.valorCampoComercial(this.oportunidade, "endereco_cliente") ||
          this.enderecoFechamentoCompleto(enderecoPessoa),
        ...enderecoPessoa,
        valor_final:
          this.valorFinalDetalhe || this.oportunidade.valor_estimado || 0,
        beneficios: this.beneficiosLivresOportunidade(),
        beneficios_livres: this.beneficiosLivresOportunidade(),
        beneficios_estruturados: clienteFinal
          ? ["treinamento_cliente_final"]
          : this.beneficiosEstruturadosOportunidade(),
        cliente_final: clienteFinal,
      };
      this.$bvModal.show("crm-detalhe-ganho-modal");
    },
    validarGanho() {
      this.ganhoForm.endereco_cliente = this.enderecoFechamentoCompleto(
        this.ganhoForm
      );

      if (!(this.ganhoForm.nome_correto || "").trim()) {
        this.$toast.error("Informe o nome correto do cliente.");
        return false;
      }

      if (this.somenteNumeros(this.ganhoForm.cnpj).length !== 14) {
        this.$toast.error("Informe um CNPJ válido.");
        return false;
      }

      if (!(this.ganhoForm.email_cliente || "").trim()) {
        this.$toast.error("Informe o e-mail do cliente.");
        return false;
      }

      if (!this.validarEnderecoGanho(this.ganhoForm)) {
        return false;
      }

      if (Number(this.ganhoForm.valor_final || 0) <= 0) {
        this.$toast.error("Informe o valor final.");
        return false;
      }

      if (
        !this.ganhoForm.cliente_final &&
        !this.beneficiosEstruturadosLista(
          this.ganhoForm.beneficios_estruturados
        ).length
      ) {
        this.$toast.error("Selecione pelo menos um benefício de regra.");
        return false;
      }

      return true;
    },
    async marcarGanha() {
      if (!this.validarGanho()) {
        return;
      }

      await crmService.marcarGanha({
        id_crm_oportunidade: this.idOportunidade,
        fechamento: {
          nome_correto: (this.ganhoForm.nome_correto || "").trim(),
          cnpj: this.ganhoForm.cnpj,
          email_cliente: (this.ganhoForm.email_cliente || "").trim(),
          telefone_cliente: (this.ganhoForm.telefone_cliente || "").trim(),
          endereco_cliente: (this.ganhoForm.endereco_cliente || "").trim(),
          cep: this.ganhoForm.cep,
          logradouro: this.ganhoForm.logradouro,
          numero: this.ganhoForm.numero,
          bairro: this.ganhoForm.bairro,
          complemento: this.ganhoForm.complemento,
          id_cidade: this.ganhoForm.id_cidade,
          valor_final: Number(this.ganhoForm.valor_final || 0),
          beneficios: (this.ganhoForm.beneficios_livres || "").trim(),
          beneficios_livres: (this.ganhoForm.beneficios_livres || "").trim(),
          beneficios_estruturados: this.ganhoForm.cliente_final
            ? ["treinamento_cliente_final"]
            : this.beneficiosEstruturadosLista(
                this.ganhoForm.beneficios_estruturados
              ),
        },
      });
      this.$toast.success("Oportunidade marcada como ganha.");
      this.$bvModal.hide("crm-detalhe-ganho-modal");
      this.afterAction();
    },
    abrirModalPerda() {
      this.perdaForm = this.emptyPerdaForm();
      this.$bvModal.show("crm-detalhe-perda-modal");
    },
    async marcarPerdida() {
      if (!this.perdaForm.id_crm_motivo_perda) {
        this.$toast.error("Informe o motivo da perda.");
        return;
      }

      await crmService.marcarPerdida({
        id_crm_oportunidade: this.idOportunidade,
        ...this.perdaForm,
      });
      this.$toast.success("Oportunidade marcada como perdida.");
      this.$bvModal.hide("crm-detalhe-perda-modal");
      this.afterAction();
    },
    async afterAction() {
      await this.loadOportunidade();
      await Promise.allSettled([
        this.loadTimeline(true),
        this.loadRelacionados(),
      ]);
    },
    erroMensagem(error, fallback) {
      const response = error && error.response ? error.response.data : null;

      return (
        (response && (response.msg || response.message)) ||
        (error && error.message) ||
        fallback
      );
    },
    focusNota() {
      this.activeTab = 3;
      this.$nextTick(() => {
        if (this.$refs.notaInput) {
          this.$refs.notaInput.focus();
        }
      });
    },
    normalizeCollection(data) {
      if (Array.isArray(data)) {
        return data;
      }

      if (data && Array.isArray(data.data)) {
        return data.data;
      }

      return [];
    },
    responseData(response) {
      const body = response && response.data ? response.data : response;
      if (body && body.status === false) {
        throw new Error(
          body.msg || body.message || "Não foi possível concluir a operação."
        );
      }
      return body && body.data ? body.data : body;
    },
    booleanFlag(value) {
      if (value === true || value === 1) {
        return true;
      }

      if (typeof value === "string") {
        return ["1", "true", "sim", "yes", "t"].includes(value.toLowerCase());
      }

      return false;
    },
    optionText(options, value) {
      const option = (options || []).find(
        (item) => String(item.value) === String(value)
      );

      return option ? option.text : null;
    },
    vendedoresFromAtribuicoes(response) {
      return this.usuariosPorPapelFromAtribuicoes(response, "vendedor");
    },
    desenvolvedoresFromAtribuicoes(response) {
      return this.usuariosPorPapelFromAtribuicoes(response, "desenvolvedor");
    },
    usuariosPorPapelFromAtribuicoes(response, papel) {
      const data = this.responseData(response);
      const usuarios =
        data && Array.isArray(data.usuarios) ? data.usuarios : [];

      return usuarios.filter((usuario) => {
        const papeis = Array.isArray(usuario.papeis_crm)
          ? usuario.papeis_crm
          : [];

        return usuario.selecionado || papeis.includes(papel);
      });
    },
    usuarioPorId(idUsuario) {
      const id = Number(idUsuario);
      const listas = [
        this.usuarios,
        this.vendedoresAtribuicao,
        this.desenvolvedoresAtribuicao,
        this.treinamentoAtribuicao,
      ];

      return listas
        .flat()
        .find((usuario) => Number(usuario.id_usuario) === id);
    },
    usuarioPossuiPapelCrm(idUsuario, papel) {
      const usuario = this.usuarioPorId(idUsuario);
      const papeis = Array.isArray(usuario && usuario.papeis_crm)
        ? usuario.papeis_crm
        : [];

      return papeis.includes(papel);
    },
    funilAtualChavePreset() {
      if (!this.oportunidade) {
        return null;
      }

      const funil = this.funis.find(
        (item) =>
          Number(item.id_crm_funil) === Number(this.oportunidade.id_crm_funil)
      );

      return funil ? funil.chave_preset : null;
    },
    usuarioTemAcessoPorPapelAoFunilAtual(idUsuario) {
      const mapa = {
        vendas: ["vendedor", "desenvolvedor"],
        vendas_cliente_final: ["vendedor", "desenvolvedor"],
        pos_venda: ["vendedor"],
        implementacao_white_label: ["desenvolvedor"],
        treinamento_cliente_final: ["treinamento_cliente_final", "treinamento"],
        treinamento: ["treinamento"],
        website: ["website"],
      };
      const papeis = mapa[this.funilAtualChavePreset()] || [];

      return papeis.some((papel) => this.usuarioPossuiPapelCrm(idUsuario, papel));
    },
    isTreinamentoClienteFinal(oportunidade) {
      return (
        oportunidade && oportunidade.tipo_fluxo === "treinamento_cliente_final"
      );
    },
    idPrimeiroResponsavelTreinamento() {
      const primeiro = this.treinamentoResponsavelOptions.find(
        (option) => option.value
      );

      return primeiro ? primeiro.value : null;
    },
    timelinePayload(item) {
      if (!item.payload) {
        return {};
      }

      if (typeof item.payload === "object") {
        return item.payload;
      }

      try {
        return JSON.parse(item.payload);
      } catch (error) {
        return {};
      }
    },
    timelineAgendavel(item) {
      return (
        item.tipo_evento === "atividade_criada" &&
        !!this.timelinePayload(item).id_crm_atividade
      );
    },
    agendarTarefaTimeline(item) {
      this.abrirModalAgenda(this.timelinePayload(item));
    },
    timelineAnexoUrl(item) {
      return this.timelinePayload(item).url || "";
    },
    timelineAnexoNome(item) {
      return this.timelinePayload(item).nome || "anexo";
    },
    tipoTarefaLabel(chave) {
      const tipo = this.tiposTarefa.find((item) => item.chave === chave);
      return tipo ? tipo.nome : chave || "-";
    },
    tipoAgendaLabel(chave) {
      const tipo = this.tipoAgendaOptions.find((item) => item.value === chave);
      return tipo ? tipo.text : chave || "-";
    },
    etapaOptions(idFunil) {
      const funil = this.funis.find((item) => item.id_crm_funil === idFunil);
      const etapas = funil && Array.isArray(funil.etapas) ? funil.etapas : [];

      return etapas.map((etapa) => ({
        value: etapa.id_crm_etapa,
        text: etapa.nome,
      }));
    },
    findEtapaById(idEtapa) {
      if (!idEtapa) {
        return null;
      }

      for (const funil of this.funis) {
        const etapas = funil.etapas || [];
        const etapa = etapas.find(
          (item) => item.id_crm_etapa === Number(idEtapa)
        );

        if (etapa) {
          return etapa;
        }
      }

      if (
        this.oportunidade &&
        Number(this.oportunidade.id_crm_etapa) === Number(idEtapa)
      ) {
        return {
          id_crm_etapa: this.oportunidade.id_crm_etapa,
          nome: this.oportunidade.etapa,
        };
      }

      return null;
    },
    camposObrigatoriosEtapa(etapa, contexto = "entrada_etapa") {
      if (!etapa) {
        return [];
      }

      return (
        etapa.campos_obrigatorios ||
        etapa.camposObrigatorios ||
        []
      ).filter(
        (regra) =>
          regra.ativo !== false &&
          (!regra.contexto || regra.contexto === contexto)
      );
    },
    campoObrigatorioConfig(regra) {
      return regra.campo || regra.campo_comercial || {};
    },
    campoObrigatorioChave(regra) {
      return this.campoObrigatorioConfig(regra).chave || regra.chave || "";
    },
    campoObrigatorioTipo(regra) {
      return this.campoObrigatorioConfig(regra).tipo || regra.tipo || "texto";
    },
    isCampoObrigatorioMonetario(regra) {
      return [
        "valor_estimado",
        "valor_proposta",
        "valor_negociacao",
        "valor_final",
      ].includes(this.campoObrigatorioChave(regra));
    },
    labelCampoObrigatorio(regra) {
      return this.campoObrigatorioConfig(regra).nome || regra.nome || "Campo";
    },
    campoObrigatorioOptions(regra) {
      const opcoes = this.campoObrigatorioConfig(regra).opcoes || [];
      const valores = Array.isArray(opcoes)
        ? opcoes
        : this.parseCampoObrigatorioOpcoes(opcoes);

      return [{ value: null, text: "Selecione" }].concat(
        valores.map((valor) =>
          typeof valor === "object"
            ? {
                value: valor.value || valor.valor || valor.label,
                text: valor.label || valor.text || valor.valor,
              }
            : { value: valor, text: valor }
        )
      );
    },
    parseCampoObrigatorioOpcoes(opcoes) {
      const texto = String(opcoes || "");

      if (!texto) {
        return [];
      }

      try {
        const parsed = JSON.parse(texto);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (error) {
        // Campo legado pode vir como texto livre separado por linha.
      }

      return texto.split("\n").filter(Boolean);
    },
    slugEtapa(nome) {
      return String(nome || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");
    },
    etapaPossuiChave(etapa, chave) {
      if (!etapa) {
        return false;
      }

      const chaves = [etapa.chave_preset, etapa.chave, etapa.nome].map(
        (valor) => this.slugEtapa(valor)
      );

      return chaves.includes(chave);
    },
    oportunidadeTemVendedorResponsavel(oportunidade = {}) {
      return !!(
        oportunidade &&
        (oportunidade.id_vendedor_responsavel ||
          this.valorCampoComercial(oportunidade, "id_vendedor_responsavel"))
      );
    },
    oportunidadeTemResponsavelTecnico(oportunidade = {}) {
      return !!(
        oportunidade &&
        (oportunidade.id_responsavel_tecnico ||
          this.valorCampoComercial(oportunidade, "id_responsavel_tecnico"))
      );
    },
    isEtapaApresentacaoTecnica(etapa) {
      if (!etapa) {
        return false;
      }

      return (
        etapa.chave_preset === "apresentacao_tecnica" ||
        this.slugEtapa(etapa.nome) === "apresentacao_tecnica"
      );
    },
    movimentoExigeAtribuicaoParaDestino(etapaDestino) {
      return (
        !this.oportunidadeTemVendedorResponsavel(this.oportunidade) &&
        this.etapaPossuiChave(this.etapaAtualConfig, "primeiro_contato") &&
        this.etapaPossuiChave(etapaDestino, "qualificacao")
      );
    },
    deveAbrirModalMovimento(etapaDestino) {
      return (
        this.camposObrigatoriosEtapa(etapaDestino).length > 0 ||
        (!this.oportunidadeTemResponsavelTecnico(this.oportunidade) &&
          this.isEtapaApresentacaoTecnica(etapaDestino)) ||
        this.movimentoExigeAtribuicaoParaDestino(etapaDestino)
      );
    },
    prepararCamposMovimento() {
      const form = {};

      this.camposObrigatoriosMovimento.forEach((regra) => {
        const idCampo = regra.id_crm_campo_comercial;
        let valor = this.movimentoCamposForm[idCampo] || "";

        if (
          this.movimentoManterValor &&
          this.campoObrigatorioChave(regra) === "valor_negociacao"
        ) {
          valor = this.valorPadraoNegociacao();
        }

        this.$set(form, idCampo, valor);
      });

      this.movimentoCamposForm = form;
    },
    valorPadraoNegociacao() {
      const valor =
        this.valorCampoComercial(this.oportunidade, "valor_proposta") ||
        this.oportunidade.valor_estimado;
      const numero = Number(valor || 0);

      return numero ? numero.toFixed(2) : "";
    },
    validarCamposMovimento() {
      if (this.movimentoExigeAtribuicao && !this.idUsuarioDestino) {
        this.$toast.error(
          "Selecione o vendedor responsável pela oportunidade."
        );
        return false;
      }

      if (this.movimentoExigeTecnico && !this.idUsuarioTecnicoDestino) {
        this.$toast.error(
          "Selecione o desenvolvedor responsável pela apresentação técnica."
        );
        return false;
      }

      if (this.usuarioDestinoSemAcesso && !this.concederAcessoFunilDestino) {
        this.$toast.error(
          "O usuário escolhido ainda não tem acesso ao funil atual."
        );
        return false;
      }

      const faltante = this.camposObrigatoriosMovimento.find((regra) => {
        const valor =
          this.movimentoCamposForm[regra.id_crm_campo_comercial] || "";
        return String(valor).trim() === "";
      });

      if (faltante) {
        this.$toast.error(
          faltante.mensagem ||
            `Preencha o campo obrigatório: ${this.labelCampoObrigatorio(
              faltante
            )}.`
        );
        return false;
      }

      return true;
    },
    movimentoCamposPayload() {
      return this.camposObrigatoriosMovimento.map((regra) => ({
        id_crm_campo_comercial: regra.id_crm_campo_comercial,
        valor: this.movimentoCamposForm[regra.id_crm_campo_comercial],
      }));
    },
    tentarMoverEtapaDetalhe(etapa) {
      if (
        !this.oportunidade ||
        this.stageChanging ||
        this.oportunidade.status !== "aberta" ||
        !etapa ||
        etapa.id_crm_etapa === this.oportunidade.id_crm_etapa
      ) {
        return;
      }

      if (this.deveAbrirModalMovimento(etapa)) {
        this.abrirModalMoverDetalhe(etapa.id_crm_etapa);
        return;
      }

      this.moverEtapaDetalhe(etapa.id_crm_etapa);
    },
    abrirModalMoverDetalhe(idEtapaDestino) {
      this.idEtapaDestino = idEtapaDestino;
      this.idUsuarioTecnicoDestino = null;
      this.idUsuarioDestino = null;
      this.concederAcessoFunilDestino = false;
      this.movimentoCamposForm = {};
      this.movimentoManterValor = true;
      this.prepararCamposMovimento();
      this.$bvModal.show("crm-detalhe-mover-etapa-modal");
    },
    confirmarMoverEtapaDetalhe() {
      if (
        !this.idEtapaDestino ||
        this.idEtapaDestino === this.oportunidade.id_crm_etapa
      ) {
        this.$bvModal.hide("crm-detalhe-mover-etapa-modal");
        return;
      }

      this.prepararCamposMovimento();

      if (!this.validarCamposMovimento()) {
        return;
      }

      this.moverEtapaDetalhe(this.idEtapaDestino, {
        id_usuario_tecnico: this.movimentoExigeTecnico
          ? this.idUsuarioTecnicoDestino
          : null,
        id_usuario_destino: this.movimentoExigeAtribuicao
          ? this.idUsuarioDestino
          : null,
        id_vendedor_responsavel: this.movimentoExigeAtribuicao
          ? this.idUsuarioDestino
          : null,
        conceder_acesso_funil: this.concederAcessoFunilDestino,
        campos_comerciais: this.movimentoCamposPayload(),
      });
    },
    async moverEtapaDetalhe(idEtapaDestino, extraPayload = {}) {
      this.stageChanging = true;

      try {
        await crmService.moverEtapa({
          id_crm_oportunidade: this.idOportunidade,
          id_crm_etapa: idEtapaDestino,
          ...extraPayload,
        });
        this.$toast.success("Etapa alterada.");
        this.$bvModal.hide("crm-detalhe-mover-etapa-modal");
        await this.afterAction();
      } catch (error) {
        this.$toast.error(
          this.apiError(error, "Não foi possível alterar a etapa.")
        );
      } finally {
        this.stageChanging = false;
      }
    },
    temperaturaLabel(valor) {
      const option = this.temperaturaOptions.find(
        (item) => item.value === valor
      );

      return option ? option.text : valor || "-";
    },
    temperaturaOption(valor) {
      return this.temperaturaOptions.find((item) => item.value === valor);
    },
    temperaturaIcon(valor) {
      const icons = {
        muito_frio: "la la-thermometer-empty",
        frio: "la la-thermometer-quarter",
        morno: "la la-thermometer-half",
        quente: "la la-thermometer-three-quarters",
        venda_certa: "la la-thermometer-full",
      };

      return icons[valor] || "la la-thermometer-empty";
    },
    temperaturaColor(valor) {
      const option = this.temperaturaOption(valor);

      return option && option.cor ? option.cor : "#7e8299";
    },
    abrirModalTemperaturaDetalhe() {
      this.temperaturaForm = {
        temperatura: this.oportunidade.temperatura || null,
      };
      this.$bvModal.show("crm-detalhe-temperatura-modal");
    },
    async salvarTemperaturaDetalhe() {
      try {
        await crmService.atualizarOportunidade({
          id_crm_oportunidade: this.idOportunidade,
          temperatura: this.temperaturaForm.temperatura || null,
        });
        this.$toast.success("Temperatura atualizada.");
        this.$bvModal.hide("crm-detalhe-temperatura-modal");
        await this.afterAction();
      } catch (error) {
        this.$toast.error(
          this.apiError(error, "Não foi possível atualizar a temperatura.")
        );
      }
    },
    temperaturaOptionsFromList(temperaturas) {
      const ativos = temperaturas.filter((item) => item.ativo !== false);

      if (!ativos.length) {
        return this.temperaturaOptions;
      }

      return [{ value: null, text: "Sem temperatura" }].concat(
        ativos.map((item) => ({
          value: item.chave,
          text: item.nome,
          cor: item.cor,
        }))
      );
    },
    campoComercialOptions(campo) {
      const opcoes = String(campo.opcoes || "")
        .split(/\r?\n/)
        .map((item) => item.trim())
        .filter(Boolean);

      return [{ value: "", text: "Selecione" }].concat(
        opcoes.map((item) => ({ value: item, text: item }))
      );
    },
    campoInputType(campo) {
      if (campo.tipo === "numero") {
        return "number";
      }

      if (campo.tipo === "data") {
        return "date";
      }

      return "text";
    },
    campoValorLabel(campo) {
      if (
        campo.valor === null ||
        campo.valor === undefined ||
        campo.valor === ""
      ) {
        return "-";
      }

      if (campo.tipo === "booleano") {
        return String(campo.valor) === "1" ? "Sim" : "Não";
      }

      if (campo.chave === "beneficios" || campo.chave === "beneficios_livres") {
        return String(campo.valor || "").trim() || "-";
      }

      if (campo.chave === "beneficios_estruturados") {
        return (
          this.beneficiosEstruturadosLista(campo.valor)
            .map((item) => this.beneficioLabel(item))
            .join(", ") || "-"
        );
      }

      return String(campo.valor);
    },
    beneficiosTexto(value) {
      let valores = value;

      if (typeof valores === "string") {
        const trimmed = valores.trim();
        if (!trimmed) {
          return "";
        }

        try {
          const parsed = JSON.parse(trimmed);
          valores = Array.isArray(parsed) ? parsed : trimmed;
        } catch (error) {
          valores = trimmed;
        }
      }

      if (!Array.isArray(valores)) {
        valores = String(valores || "")
          .split(/[,;\n]+/)
          .map((item) => item.trim());
      }

      return this.normalizarBeneficiosLista(valores).join(", ");
    },
    beneficiosLista(value) {
      let valores = value;

      if (typeof valores === "string") {
        const trimmed = valores.trim();
        if (!trimmed) {
          return [];
        }

        try {
          const parsed = JSON.parse(trimmed);
          valores = Array.isArray(parsed) ? parsed : trimmed;
        } catch (error) {
          valores = trimmed;
        }
      }

      if (!Array.isArray(valores)) {
        valores = String(valores || "")
          .split(/[,;\n]+/)
          .map((item) => item.trim());
      }

      return this.normalizarBeneficiosLista(valores);
    },
    normalizarBeneficiosLista(valores) {
      const lista = valores
        .map((item) => this.beneficioLabel(item))
        .filter(Boolean)
        .filter((item, index, array) => array.indexOf(item) === index);

      return lista.includes("Treinamento Cliente Final")
        ? lista.filter((item) => item !== "Treinamento")
        : lista;
    },
    beneficioLabel(value) {
      const text = String(value || "").trim();
      if (!text) {
        return "";
      }

      const key = text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
      const labels = {
        website: "Website",
        web_site: "Website",
        site: "Website",
        treinamento: "Treinamento",
        treinamento_cliente_final: "Treinamento Cliente Final",
        treinamento_final: "Treinamento Cliente Final",
        cliente_final: "Treinamento Cliente Final",
        desconto: "Desconto",
        implantacao: "Implementação White Label",
        implementacao: "Implementação White Label",
        implantacao_white_label: "Implementação White Label",
        implementacao_white_label: "Implementação White Label",
        white_label: "Implementação White Label",
        suporte_estendido: "Suporte estendido",
      };

      return labels[key] || text.replace(/_/g, " ");
    },
    beneficioKey(value) {
      const text = String(value || "").trim();
      if (!text) {
        return "";
      }

      const key = text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
      const keys = {
        website: "website",
        web_site: "website",
        site: "website",
        implantacao: "implementacao_white_label",
        implementacao: "implementacao_white_label",
        implantacao_white_label: "implementacao_white_label",
        implementacao_white_label: "implementacao_white_label",
        white_label: "implementacao_white_label",
        treinamento_cliente_final: "treinamento_cliente_final",
        treinamento_final: "treinamento_cliente_final",
        cliente_final: "treinamento_cliente_final",
      };

      return keys[key] || "";
    },
    beneficiosEstruturadosLista(value) {
      let valores = value;

      if (typeof valores === "string") {
        const trimmed = valores.trim();
        if (!trimmed) {
          return [];
        }

        try {
          const parsed = JSON.parse(trimmed);
          valores = Array.isArray(parsed) ? parsed : trimmed;
        } catch (error) {
          valores = trimmed;
        }
      }

      if (!Array.isArray(valores)) {
        valores = String(valores || "")
          .split(/[,;\n]+/)
          .map((item) => item.trim());
      }

      const permitidos = this.beneficioOptions.map((item) => item.value);
      return valores
        .map((item) => this.beneficioKey(item))
        .filter((item) => permitidos.includes(item))
        .filter((item, index, array) => array.indexOf(item) === index);
    },
    beneficiosEstruturadosOportunidade() {
      const valorEstruturado = this.valorCampoComercial(
        this.oportunidade,
        "beneficios_estruturados"
      );
      const lista = this.beneficiosEstruturadosLista(valorEstruturado);

      if (lista.length || valorEstruturado) {
        return lista;
      }

      return this.beneficiosEstruturadosLista(
        this.valorCampoComercial(this.oportunidade, "beneficios")
      );
    },
    beneficiosLivresOportunidade() {
      return String(
        this.valorCampoComercial(this.oportunidade, "beneficios_livres") ||
          this.valorCampoComercial(this.oportunidade, "beneficios") ||
          ""
      ).trim();
    },
    beneficioEstruturadoSelecionado(value) {
      return this.beneficiosEstruturadosLista(
        this.ganhoForm.beneficios_estruturados
      ).includes(value);
    },
    toggleBeneficioGanho(value) {
      const key = this.beneficioKey(value);
      if (!key) {
        return;
      }

      let atuais = this.beneficiosEstruturadosLista(
        this.ganhoForm.beneficios_estruturados
      );

      if (atuais.includes(key)) {
        this.ganhoForm.beneficios_estruturados = atuais.filter(
          (item) => item !== key
        );
        return;
      }

      if (key === "treinamento_cliente_final") {
        this.ganhoForm.beneficios_estruturados = [key];
        return;
      }

      atuais = atuais.filter((item) => item !== "treinamento_cliente_final");
      atuais.push(key);
      this.ganhoForm.beneficios_estruturados = atuais;
    },
    beneficioCampoSelecionado(idCampo, value) {
      return this.beneficiosEstruturadosLista(
        this.dadosForm.campos_comerciais[idCampo]
      ).includes(value);
    },
    toggleBeneficioCampo(idCampo, value) {
      const key = this.beneficioKey(value);
      if (!key) {
        return;
      }

      let atuais = this.beneficiosEstruturadosLista(
        this.dadosForm.campos_comerciais[idCampo]
      );

      if (atuais.includes(key)) {
        this.$set(
          this.dadosForm.campos_comerciais,
          idCampo,
          atuais.filter((item) => item !== key)
        );
        return;
      }

      if (key === "treinamento_cliente_final") {
        this.$set(this.dadosForm.campos_comerciais, idCampo, [key]);
        return;
      }

      atuais = atuais.filter((item) => item !== "treinamento_cliente_final");
      atuais.push(key);
      this.$set(this.dadosForm.campos_comerciais, idCampo, atuais);
    },
    adicionarBeneficioGanho(label) {
      this.toggleBeneficioGanho(label);
    },
    adicionarBeneficioCampo(idCampo, label) {
      this.toggleBeneficioCampo(idCampo, label);
    },
    async consultarCnpjFechamento() {
      if (this.somenteNumeros(this.ganhoForm.cnpj).length !== 14) {
        this.$toast.warning("Informe um CNPJ válido para consultar.");
        return;
      }

      this.loadingConsultaCnpjFechamento = true;

      try {
        const receita = await this.getConsultaReceita(this.ganhoForm.cnpj);
        this.aplicarReceitaFechamento(receita);
        this.$toast.success(
          "Dados do CNPJ carregados. Confira antes de fechar."
        );
      } catch (error) {
        this.$toast.warning(
          this.apiError(error, "Não foi possível consultar o CNPJ.")
        );
      } finally {
        this.loadingConsultaCnpjFechamento = false;
      }
    },
    aplicarReceitaFechamento(receita = {}) {
      this.ganhoForm.nome_correto =
        receita.nome || this.ganhoForm.nome_correto || "";

      if (receita.email && receita.email.email) {
        this.ganhoForm.email_cliente = receita.email.email;
      }

      if (receita.telefone && receita.telefone.fone) {
        this.ganhoForm.telefone_cliente = receita.telefone.fone;
      }

      if (receita.endereco && receita.endereco.cep) {
        this.ganhoForm.cep = receita.endereco.cep || "";
        this.ganhoForm.logradouro = receita.endereco.logradouro || "";
        this.ganhoForm.numero = receita.endereco.numero || "";
        this.ganhoForm.bairro = receita.endereco.bairro || "";
        this.ganhoForm.complemento = receita.endereco.complemento || "";
        this.ganhoForm.id_cidade = receita.endereco.id_cidade || null;
        this.ganhoForm.cidade =
          receita.endereco.cidade && receita.endereco.cidade.cidade
            ? receita.endereco.cidade.cidade
            : "";
        this.ganhoForm.uf =
          receita.endereco.cidade && receita.endereco.cidade.uf
            ? receita.endereco.cidade.uf
            : "";
        this.ganhoForm.endereco_cliente = this.enderecoFechamentoCompleto(
          this.ganhoForm
        );
      }
    },
    async consultarCepFechamento() {
      if (this.somenteNumeros(this.ganhoForm.cep).length !== 8) {
        this.$toast.warning("Informe um CEP válido para consultar.");
        return;
      }

      this.loadingConsultaCepFechamento = true;

      try {
        const cep = await this.getCep(this.ganhoForm.cep);

        if (!cep || Array.isArray(cep) || cep.erro) {
          this.$toast.warning("Confira o CEP informado.");
          return;
        }

        this.ganhoForm.logradouro = cep.logradouro || "";
        this.ganhoForm.bairro = cep.bairro || "";
        this.ganhoForm.complemento = cep.complemento || "";
        this.ganhoForm.cep = cep.cep || this.ganhoForm.cep;

        const cidade = await this.getCidadeByUF(cep.localidade, cep.uf);
        const cidadeSelecionada = this.primeiraCidadeConsulta(cidade);
        this.ganhoForm.id_cidade = cidadeSelecionada
          ? cidadeSelecionada.id_cidade
          : null;
        this.ganhoForm.cidade = cidadeSelecionada
          ? cidadeSelecionada.cidade
          : cep.localidade || "";
        this.ganhoForm.uf = cidadeSelecionada
          ? cidadeSelecionada.uf
          : cep.uf || "";
        this.ganhoForm.endereco_cliente = this.enderecoFechamentoCompleto(
          this.ganhoForm
        );
        this.$toast.success("Endereço carregado pelo CEP.");
      } catch (error) {
        this.$toast.warning(
          this.apiError(error, "Não foi possível consultar o CEP.")
        );
      } finally {
        this.loadingConsultaCepFechamento = false;
      }
    },
    primeiraCidadeConsulta(cidade) {
      return Array.isArray(cidade) ? cidade[0] : cidade;
    },
    enderecoPessoaOportunidade(oportunidade) {
      return {
        cep: oportunidade.pessoa_endereco_cep || "",
        logradouro: oportunidade.pessoa_endereco_logradouro || "",
        numero: oportunidade.pessoa_endereco_numero || "",
        bairro: oportunidade.pessoa_endereco_bairro || "",
        complemento: oportunidade.pessoa_endereco_complemento || "",
        id_cidade: oportunidade.pessoa_endereco_id_cidade || null,
        cidade: oportunidade.pessoa_endereco_cidade || "",
        uf: oportunidade.pessoa_endereco_uf || "",
      };
    },
    validarEnderecoGanho(form) {
      const enderecoTexto = (form.endereco_cliente || "").trim();
      const camposEstruturados = [
        form.cep,
        form.id_cidade,
        form.logradouro,
        form.numero,
        form.bairro,
      ];
      const temCampoEstruturado = camposEstruturados.some((valor) =>
        (valor || "").toString().trim()
      );

      if (!enderecoTexto && !temCampoEstruturado) {
        this.$toast.error("Informe o endereço do cliente.");
        return false;
      }

      if (!temCampoEstruturado && enderecoTexto) {
        return true;
      }

      if (this.somenteNumeros(form.cep).length !== 8) {
        this.$toast.error("Informe um CEP válido.");
        return false;
      }

      if (!form.id_cidade) {
        this.$toast.error("Informe a cidade do cliente pelo CEP.");
        return false;
      }

      if (!(form.logradouro || "").trim()) {
        this.$toast.error("Informe o logradouro do cliente.");
        return false;
      }

      if (!(form.numero || "").trim()) {
        this.$toast.error("Informe o número do endereço do cliente.");
        return false;
      }

      if (!(form.bairro || "").trim()) {
        this.$toast.error("Informe o bairro do cliente.");
        return false;
      }

      return true;
    },
    enderecoFechamentoCompleto(form) {
      const endereco = [
        form.logradouro,
        form.numero ? `nº ${form.numero}` : null,
        form.bairro,
        [form.cidade, form.uf].filter(Boolean).join(" / "),
        form.cep ? `CEP ${form.cep}` : null,
      ]
        .filter(Boolean)
        .join(", ");

      return endereco || form.endereco_cliente || "";
    },
    camposComerciaisFormValues() {
      return this.camposComerciaisDetalhe.reduce((values, campo) => {
        if (campo.chave === "beneficios_estruturados") {
          values[campo.id_crm_campo_comercial] =
            this.beneficiosEstruturadosLista(campo.valor);
          return values;
        }

        values[campo.id_crm_campo_comercial] =
          campo.valor === null || campo.valor === undefined ? "" : campo.valor;

        return values;
      }, {});
    },
    camposComerciaisPayload() {
      return this.camposComerciaisDetalhe.map((campo) => {
        const valor =
          this.dadosForm.campos_comerciais[campo.id_crm_campo_comercial];

        return {
          id_crm_campo_comercial: campo.id_crm_campo_comercial,
          valor: this.normalizarValorCampoComercialPayload(campo, valor),
        };
      });
    },
    normalizarValorCampoComercialPayload(campo, valor) {
      if (campo.chave === "beneficios_estruturados") {
        const beneficios = this.beneficiosEstruturadosLista(valor);
        return beneficios.length ? beneficios : null;
      }

      if (campo.chave === "beneficios" || campo.chave === "beneficios_livres") {
        return String(valor || "").trim() || null;
      }

      return valor === null || valor === undefined || valor === ""
        ? null
        : valor;
    },
    atividadeStatusLabel(status) {
      const labels = {
        aberta: "Aberta",
        concluida: "Concluída",
        adiada: "Adiada",
        cancelada: "Cancelada",
      };

      return labels[status] || status || "-";
    },
    solicitacaoTecnicaStatusLabel(status) {
      const labels = {
        solicitada: "Aguardando técnico",
        link_enviado: "Aguardando cliente agendar",
        agendada: "Agendada",
        concluida: "Concluída",
        cancelada: "Cancelada",
      };

      return labels[status] || status || "-";
    },
    solicitacaoTecnicaFinalizada(solicitacao) {
      return ["concluida", "cancelada"].includes(solicitacao?.status);
    },
    agendaStatusLabel(status) {
      const labels = {
        agendado: "Agendado",
        confirmado: "Confirmado",
        concluido: "Concluído",
        realizado: "Realizado",
        cancelado: "Cancelado",
        nao_compareceu: "Não compareceu",
      };

      return labels[status] || status || "-";
    },
    agendaStatusFinal(status) {
      return ["concluido", "realizado", "cancelado", "nao_compareceu"].includes(
        status
      );
    },
    tarefaTemAgendaAtiva(tarefa) {
      return (
        !!tarefa?.agenda_ativa_id_crm_agenda ||
        this.booleanFlag(tarefa?.tarefa_agendada)
      );
    },
    agendaAtivaDaTarefa(tarefa) {
      const idAgenda = tarefa?.agenda_ativa_id_crm_agenda;

      if (!idAgenda) {
        return null;
      }

      const agendaCarregada = this.agendas.find(
        (agenda) => Number(agenda.id_crm_agenda) === Number(idAgenda)
      );

      if (agendaCarregada) {
        return agendaCarregada;
      }

      return {
        id_crm_agenda: idAgenda,
        id_crm_atividade: tarefa.id_crm_atividade,
        id_usuario: tarefa.agenda_ativa_id_usuario || tarefa.id_usuario,
        data_inicio: tarefa.agenda_ativa_data_inicio || tarefa.vencimento_em,
        duracao_minutos: tarefa.agenda_ativa_duracao_minutos || 30,
        status: "agendado",
        pode_reagendar: true,
      };
    },
    podeReagendarAgenda(agenda) {
      return (
        !this.agendaStatusFinal(agenda.status) &&
        agenda.pode_reagendar !== false
      );
    },
    podeCancelarAgenda(agenda) {
      return (
        !this.agendaStatusFinal(agenda.status) && agenda.pode_cancelar !== false
      );
    },
    podeConcluirAgenda(agenda) {
      return (
        !this.agendaStatusFinal(agenda.status) && agenda.pode_concluir !== false
      );
    },
    atividadeAtrasada(atividade) {
      return (
        atividade.status === "aberta" &&
        atividade.vencimento_em &&
        new Date(atividade.vencimento_em) < new Date()
      );
    },
    statusLabel(status) {
      const labels = {
        aberta: "Aberta",
        ganha: "Ganha",
        perdida: "Perdida",
      };

      return labels[status] || status || "-";
    },
    statusClass(status) {
      if (status === "ganha") {
        return "success";
      }

      if (status === "perdida") {
        return "danger";
      }

      return "primary";
    },
    agruparTimelinePorDia(items) {
      const grupos = [];
      const porChave = {};

      items.forEach((item) => {
        const data = item.created_at ? new Date(item.created_at) : new Date();
        const key = data.toISOString().slice(0, 10);
        const label = data.toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });

        if (!porChave[key]) {
          porChave[key] = {
            key,
            label,
            items: [],
          };
          grupos.push(porChave[key]);
        }

        porChave[key].items.push(item);
      });

      return grupos;
    },
    timelineCategoriaEvento(tipo) {
      const grupos = {
        alteracoes: [
          "oportunidade_criada",
          "oportunidade_atualizada",
          "etapa_alterada",
          "responsavel_alterado",
          "vendedor_responsavel_definido",
          "responsavel_tecnico_definido",
          "pessoa_atualizada_fechamento",
          "oportunidade_ganha",
          "oportunidade_perdida",
          "contrato_criado",
          "contrato_assinado",
          "implementacao_criada",
          "implementacao_finalizada",
          "treinamento_criado",
          "treinamento_concluido",
          "treinamento_finalizado",
          "website_criado",
          "website_pronto",
          "website_finalizado",
          "pos_venda_criado",
          "automacao_sucesso",
          "automacao_erro",
          "automacao_ignorada",
          "automacao_executada",
          "perda_registrada",
        ],
        notas: ["nota", "nota_adicionada"],
        anexos: ["anexo_adicionado"],
        tarefas: [
          "atividade_criada",
          "atividade_concluida",
          "atividade_adiada",
          "atividade_lembrete_adiado",
        ],
        agenda: [
          "crm_agenda_criada",
          "crm_agenda_atualizada",
          "crm_agenda_reagendada",
          "crm_agenda_cancelada",
          "crm_agenda_status",
          "agendamento_criado",
          "agendamento_atualizado",
          "agendamento_reagendado",
          "agendamento_cancelado",
          "agendamento_concluido",
          "agendamento_vinculado",
          "solicitacao_tecnica_criada",
          "solicitacao_tecnica_link_enviado",
          "solicitacao_tecnica_agendada",
          "solicitacao_tecnica_concluida",
          "treinamento_agendado",
          "website_agendado",
        ],
        contato: [
          "contato_criado",
          "contato_atualizado",
          "contato_vinculado_pessoa",
        ],
        whatsapp: ["whatsapp_enviado", "whatsapp_recebido", "whatsapp_aberto"],
      };

      return (
        Object.keys(grupos).find((categoria) =>
          grupos[categoria].includes(tipo)
        ) || "alteracoes"
      );
    },
    timelineCategoriaLabel(tipo) {
      const labels = {
        alteracoes: "Alterações",
        notas: "Notas",
        tarefas: "Tarefas",
        agenda: "Agenda",
        contato: "Contato",
        whatsapp: "WhatsApp",
        anexos: "Anexos",
      };

      return labels[this.timelineCategoriaEvento(tipo)] || "Evento";
    },
    timelineTitle(tipo) {
      const labels = {
        nota: "Nota adicionada",
        nota_adicionada: "Nota adicionada",
        anexo_adicionado: "Anexo adicionado",
        atividade_criada: "Tarefa criada",
        atividade_concluida: "Tarefa concluída",
        atividade_adiada: "Tarefa adiada",
        atividade_lembrete_adiado: "Lembrete adiado",
        crm_agenda_criada: "Agendamento criado",
        crm_agenda_atualizada: "Agendamento atualizado",
        crm_agenda_reagendada: "Agendamento reagendado",
        crm_agenda_cancelada: "Agendamento cancelado",
        crm_agenda_status: "Status do agendamento atualizado",
        agendamento_criado: "Agendamento operacional criado",
        agendamento_atualizado: "Agendamento operacional atualizado",
        agendamento_reagendado: "Agendamento operacional reagendado",
        agendamento_cancelado: "Agendamento operacional cancelado",
        agendamento_concluido: "Agendamento operacional concluído",
        agendamento_vinculado: "Agendamento vinculado",
        contato_criado: "Contato criado",
        contato_atualizado: "Contato atualizado",
        contato_vinculado_pessoa: "Contato associado a cliente",
        whatsapp_enviado: "WhatsApp enviado",
        whatsapp_recebido: "WhatsApp recebido",
        whatsapp_aberto: "WhatsApp aberto",
        etapa_alterada: "Etapa alterada",
        responsavel_alterado: "Responsável alterado",
        vendedor_responsavel_definido: "Vendedor responsável definido",
        responsavel_tecnico_definido: "Responsável técnico definido",
        pessoa_atualizada_fechamento: "Pessoa atualizada no fechamento",
        oportunidade_criada: "Oportunidade criada",
        oportunidade_atualizada: "Oportunidade atualizada",
        oportunidade_ganha: "Oportunidade ganha",
        oportunidade_perdida: "Oportunidade perdida",
        contrato_criado: "Contrato criado",
        contrato_assinado: "Contrato assinado",
        implementacao_criada: "Implementação criada",
        implementacao_finalizada: "Implementação finalizada",
        treinamento_criado: "Treinamento criado",
        treinamento_concluido: "Treinamento concluído",
        treinamento_agendado: "Treinamento agendado",
        treinamento_finalizado: "Treinamento finalizado",
        website_criado: "Website criado",
        website_pronto: "Website pronto",
        website_agendado: "Website agendado",
        website_finalizado: "Website finalizado",
        pos_venda_criado: "Pós-venda criado",
        solicitacao_tecnica_criada: "Solicitação técnica criada",
        solicitacao_tecnica_link_enviado: "Link de agenda técnica enviado",
        solicitacao_tecnica_agendada: "Agenda técnica agendada",
        solicitacao_tecnica_concluida: "Solicitação técnica concluída",
        automacao_sucesso: "Automação executada",
        automacao_erro: "Erro na automação",
        automacao_ignorada: "Automação ignorada",
        automacao_executada: "Automação executada",
        perda_registrada: "Perda registrada",
      };

      return labels[tipo] || tipo || "Evento";
    },
    timelineStatus(tipo) {
      const sucesso = [
        "oportunidade_ganha",
        "atividade_concluida",
        "crm_agenda_criada",
        "agendamento_concluido",
        "solicitacao_tecnica_agendada",
        "solicitacao_tecnica_concluida",
        "contrato_assinado",
        "implementacao_finalizada",
        "treinamento_agendado",
        "treinamento_concluido",
        "treinamento_finalizado",
        "website_agendado",
        "website_pronto",
        "website_finalizado",
        "pos_venda_criado",
        "pessoa_atualizada_fechamento",
        "automacao_sucesso",
        "automacao_executada",
      ];

      if (sucesso.indexOf(tipo) >= 0) {
        return "success";
      }

      const perigo = [
        "oportunidade_perdida",
        "crm_agenda_cancelada",
        "agendamento_cancelado",
        "automacao_erro",
        "perda_registrada",
      ];

      if (perigo.indexOf(tipo) >= 0) {
        return "danger";
      }

      const aviso = [
        "atividade_adiada",
        "atividade_lembrete_adiado",
        "crm_agenda_reagendada",
        "agendamento_reagendado",
        "solicitacao_tecnica_link_enviado",
        "automacao_ignorada",
      ];

      if (aviso.indexOf(tipo) >= 0) {
        return "warning";
      }

      return "";
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatMoneyOrDash(value) {
      return value === null || value === undefined || value === ""
        ? "-"
        : this.formatMoney(value);
    },
    valorPrincipalOportunidade(oportunidade = {}) {
      const campos = [
        oportunidade.valor_principal,
        oportunidade.valor_final,
        oportunidade.valor_negociacao,
        oportunidade.valor_proposta,
        oportunidade.valor_estimado,
      ];
      const valor = campos.find(
        (item) => item !== null && item !== undefined && item !== ""
      );

      return Number(valor || 0);
    },
    valorCampoComercial(oportunidade = {}, chave) {
      if (!oportunidade) {
        return null;
      }

      if (oportunidade[chave] !== null && oportunidade[chave] !== undefined) {
        return oportunidade[chave];
      }

      const campos = Array.isArray(oportunidade.campos_comerciais)
        ? oportunidade.campos_comerciais
        : [];
      const campo = campos.find((item) => item.chave === chave);

      return campo && campo.valor !== "" && campo.valor !== null
        ? campo.valor
        : null;
    },
    somenteNumeros(value) {
      return String(value || "").replace(/\D+/g, "");
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString("pt-BR") : "-";
    },
    formatDateTime(value) {
      return value ? new Date(value).toLocaleString("pt-BR") : "-";
    },
    formatTime(value) {
      return new Date(value).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    toDateInput(value) {
      return value ? String(value).slice(0, 10) : null;
    },
    normalizarTelefoneWhatsapp(value) {
      let telefone = String(value || "").replace(/\D+/g, "");

      if (!telefone) {
        return "";
      }

      if (!telefone.startsWith("55") && telefone.length <= 11) {
        telefone = `55${telefone}`;
      }

      return telefone;
    },
    toDateTimeLocal(value) {
      if (!value) {
        return null;
      }

      const date = new Date(value);
      date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
      return date.toISOString().slice(0, 16);
    },
    fromDateTimeLocal(value) {
      return value ? value.replace("T", " ") + ":00" : null;
    },
    dateFromDateTimeLocal(value) {
      return value ? value.slice(0, 10) : null;
    },
    periodoAgendaDetalhe() {
      const inicio = new Date();
      inicio.setMonth(inicio.getMonth() - 6);

      const fim = new Date();
      fim.setMonth(fim.getMonth() + 12);

      return {
        data_inicio: this.dateOnly(inicio),
        data_fim: this.dateOnly(fim),
      };
    },
    periodoLinkSolicitacaoTecnica() {
      const inicio = new Date();
      const fim = new Date();
      fim.setDate(fim.getDate() + 7);

      return {
        data_inicio: this.dateOnly(inicio),
        data_fim: this.dateOnly(fim),
      };
    },
    dateOnly(date) {
      const normalized = new Date(date);
      normalized.setMinutes(
        normalized.getMinutes() - normalized.getTimezoneOffset()
      );
      return normalized.toISOString().slice(0, 10);
    },
    apiError(error, fallback) {
      return (
        error?.response?.data?.msg || error?.response?.data?.message || fallback
      );
    },
  },
};
</script>

<style scoped>
.crm-page-header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 20px;
}

.crm-header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.crm-opportunity {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.crm-opportunity-hero,
.crm-panel {
  background: #ffffff;
  border: 1px solid #e4e9f1;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(23, 31, 56, 0.04);
}

.crm-opportunity-hero {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 20px 22px;
}

.crm-opportunity-hero > div:first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.crm-eyebrow {
  color: #0b87c9;
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.crm-opportunity-hero h2 {
  color: #181c32;
  font-size: 25px;
  font-weight: 800;
  margin: 0;
  overflow-wrap: anywhere;
}

.crm-hero-meta {
  color: #647084;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.crm-hero-meta span {
  align-items: center;
  display: inline-flex;
  gap: 8px;
}

.crm-hero-meta span + span::before {
  background: #cad4e4;
  border-radius: 50%;
  content: "";
  height: 4px;
  width: 4px;
}

.crm-hero-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  max-width: 640px;
  min-width: 320px;
}

.crm-flow-alert {
  align-items: center;
  background: #fff8e6;
  border: 1px solid #ffe2a8;
  border-radius: 8px;
  color: #8a5a00;
  display: flex;
  font-weight: 700;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px 14px;
}

.crm-action-group {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e4e9f1;
  border-radius: 8px;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  padding: 5px;
}

.crm-action-button {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dfe6ef;
  border-radius: 6px;
  color: #25324b;
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  justify-content: center;
  line-height: 1;
  min-height: 32px;
  min-width: 0;
  padding: 7px 10px;
  transition: background 0.16s ease, border-color 0.16s ease,
    box-shadow 0.16s ease, color 0.16s ease;
}

.crm-action-button i {
  color: #0b87c9;
  font-size: 15px;
  line-height: 1;
}

.crm-action-button:hover:not(:disabled),
.crm-action-button:focus:not(:disabled) {
  background: #f4fbff;
  border-color: #9ed8f5;
  box-shadow: 0 0 0 3px rgba(11, 135, 201, 0.1);
  color: #0b5f8f;
  outline: none;
}

.crm-action-button:disabled {
  background: #f3f6f9;
  color: #a0a8b7;
  cursor: not-allowed;
}

.crm-action-button:disabled i {
  color: #a0a8b7;
}

.crm-action-button.is-success i {
  color: #1b7f65;
}

.crm-action-button.is-success:hover:not(:disabled),
.crm-action-button.is-success:focus:not(:disabled) {
  background: #f0fff7;
  border-color: #9be7c8;
  color: #146a52;
}

.crm-action-button.is-danger i {
  color: #b4233c;
}

.crm-action-button.is-danger:hover:not(:disabled),
.crm-action-button.is-danger:focus:not(:disabled) {
  background: #fff6f8;
  border-color: #ffc7d2;
  color: #9f1f36;
}

.crm-action-button.is-small {
  min-height: 30px;
  padding: 6px 9px;
}

.crm-status-pill {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  min-height: 32px;
  padding: 6px 12px;
}

.crm-status-pill.primary {
  background: #ecfbff;
  color: #0b87c9;
}

.crm-status-pill.success {
  background: #e8fff3;
  color: #1b7f65;
}

.crm-status-pill.danger {
  background: #fff5f8;
  color: #b4233c;
}

.crm-stage-bar {
  display: grid;
  gap: 6px;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  overflow-x: auto;
  padding-bottom: 2px;
}

.crm-stage-item {
  align-items: center;
  background: #eef2f7;
  border: 1px solid #dfe5ee;
  color: #52637a;
  cursor: pointer;
  display: flex;
  gap: 9px;
  min-height: 42px;
  min-width: 0;
  padding: 9px 12px;
  position: relative;
  text-align: left;
  width: 100%;
}

.crm-stage-item:disabled {
  cursor: default;
}

.crm-stage-item:not(:disabled):hover,
.crm-stage-item:not(:disabled):focus {
  background: #f7fcff;
  border-color: #0bb7d4;
  box-shadow: 0 0 0 3px rgba(11, 183, 212, 0.12);
  outline: none;
}

.crm-stage-item:first-child {
  border-radius: 8px 0 0 8px;
}

.crm-stage-item:last-child {
  border-radius: 0 8px 8px 0;
}

.crm-stage-item span {
  align-items: center;
  background: #ffffff;
  border-radius: 50%;
  display: inline-flex;
  flex: 0 0 24px;
  font-size: 12px;
  font-weight: 800;
  height: 24px;
  justify-content: center;
}

.crm-stage-item strong {
  font-size: 12px;
  min-width: 0;
  overflow-wrap: anywhere;
}

.crm-stage-item.done {
  background: #dff8f7;
  border-color: #bceceb;
  color: #146a65;
}

.crm-stage-item.active {
  background: #0bb7d4;
  border-color: #0bb7d4;
  color: #ffffff;
}

.crm-stage-item.active span {
  color: #0b87c9;
}

.crm-opportunity-layout {
  align-items: flex-start;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(280px, 340px) minmax(0, 1fr);
  min-width: 0;
}

.crm-opportunity-side,
.crm-opportunity-main {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.crm-panel {
  padding: 18px;
}

.crm-panel-header {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 14px;
}

.crm-panel-header > div {
  min-width: 0;
}

.crm-panel-header h3 {
  color: #181c32;
  font-size: 18px;
  font-weight: 800;
  margin: 0;
}

.crm-panel-header span {
  color: #7b8499;
  font-size: 12px;
}

.crm-close-summary {
  background: #f3f6f9;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 16px;
  padding: 12px;
}

.crm-close-summary span {
  color: #7e8299;
  font-size: 12px;
}

.crm-link-modal {
  display: grid;
  gap: 14px;
}

.crm-link-summary {
  background: #f8fafc;
  border: 1px solid #e4e9f1;
  border-radius: 8px;
  display: grid;
  gap: 3px;
  padding: 12px 14px;
}

.crm-link-summary span {
  color: #0b87c9;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.crm-link-summary strong {
  color: #25324b;
  font-size: 16px;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.crm-link-summary small {
  color: #647084;
  font-size: 12px;
}

.crm-contract-destination-summary {
  background: #f8fafc;
  border: 1px solid #e4e9f1;
  border-radius: 8px;
  display: grid;
  gap: 8px;
  margin-bottom: 14px;
  padding: 12px 14px;
}

.crm-contract-destination-summary div {
  display: grid;
  gap: 2px;
}

.crm-contract-destination-summary span {
  color: #7e8299;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.crm-contract-destination-summary strong {
  color: #25324b;
  font-size: 14px;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.crm-contract-responsibles-form {
  display: grid;
  gap: 14px;
}

.crm-contract-responsibles-form label {
  color: #25324b;
  display: grid;
  font-size: 13px;
  font-weight: 700;
  gap: 6px;
  margin: 0;
}

.crm-contract-responsibles-form label > span {
  color: #3f4254;
}

.crm-contract-responsibles-form select {
  min-height: 40px;
}

.crm-contract-responsibles-form small {
  color: #f64e60;
  font-size: 12px;
  font-weight: 700;
}

.crm-link-result {
  background: #f6fbff;
  border: 1px solid #cce8ff;
  border-radius: 8px;
  display: grid;
  gap: 8px;
  padding: 12px;
}

.crm-link-result span {
  color: #25324b;
  font-size: 12px;
  font-weight: 800;
}

.crm-link-result > div {
  align-items: stretch;
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr) auto;
}

.crm-link-result input {
  background: #ffffff;
  border: 1px solid #dfe6ef;
  border-radius: 6px;
  color: #25324b;
  min-width: 0;
  padding: 8px 10px;
}

.crm-link-modal-actions {
  align-items: center;
  border-top: 1px solid #edf1f6;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 14px;
}

.crm-negotiation-data {
  display: grid;
  gap: 10px;
}

.crm-negotiation-data > div {
  background: #f8fafc;
  border: 1px solid #e4e9f1;
  border-radius: 6px;
  display: grid;
  gap: 3px;
  padding: 10px 12px;
}

.crm-negotiation-data span {
  color: #7e8299;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.crm-negotiation-data strong {
  color: #25324b;
  overflow-wrap: anywhere;
}

.crm-detail-row {
  align-items: center;
  border-bottom: 1px solid #ebedf3;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 10px 0;
}

.crm-detail-row:last-child {
  border-bottom: 0;
}

.crm-detail-row span {
  color: #647084;
  min-width: 0;
  overflow-wrap: anywhere;
}

.crm-detail-row strong {
  color: #25324b;
  max-width: 55%;
  overflow-wrap: anywhere;
  text-align: right;
}

.crm-temperature-button {
  align-items: center;
  background: #ffffff;
  border: 1px solid var(--temperature-color, #dfe5ee);
  border-radius: 6px;
  color: var(--temperature-color, #25324b);
  display: inline-flex;
  gap: 6px;
  justify-content: flex-end;
  max-width: 58%;
  min-height: 30px;
  min-width: 0;
  padding: 5px 8px;
}

.crm-temperature-button strong {
  color: inherit;
  max-width: none;
  overflow-wrap: anywhere;
  text-align: right;
}

.crm-temperature-button:hover,
.crm-temperature-button:focus {
  box-shadow: 0 0 0 3px rgba(11, 183, 212, 0.14);
  outline: none;
}

.crm-temperature-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.crm-temperature-option {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dfe5ee;
  border-left: 4px solid var(--temperature-color, #7e8299);
  border-radius: 8px;
  color: #25324b;
  display: flex;
  gap: 10px;
  min-height: 44px;
  min-width: 0;
  padding: 10px 12px;
  text-align: left;
}

.crm-temperature-option i {
  color: var(--temperature-color, #7e8299);
  font-size: 18px;
}

.crm-temperature-option span {
  font-weight: 800;
  min-width: 0;
  overflow-wrap: anywhere;
}

.crm-temperature-option:hover,
.crm-temperature-option:focus {
  border-color: var(--temperature-color, #0bb7d4);
  box-shadow: 0 0 0 3px rgba(11, 183, 212, 0.12);
  outline: none;
}

.crm-temperature-option.active {
  background: #f7fcff;
  border-color: var(--temperature-color, #0bb7d4);
  box-shadow: inset 0 0 0 1px var(--temperature-color, #0bb7d4);
}

.crm-contact-card {
  background: #f7f9fc;
  border: 1px solid #e4e9f1;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 12px 14px;
}

.crm-contact-card span {
  color: #647084;
  font-size: 12px;
}

.crm-contact-card strong {
  color: #25324b;
  overflow-wrap: anywhere;
}

.crm-rule-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.crm-rule-pill {
  background: #e1f0ff;
  border-radius: 999px;
  color: #1769aa;
  font-size: 12px;
  padding: 6px 10px;
}

.crm-extra-fields {
  background: #f7f9fc;
  border: 1px solid #e4e9f1;
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 14px;
}

.crm-extra-fields h4 {
  color: #25324b;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 12px;
}

.crm-contact-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.crm-tabs-panel {
  padding: 0;
}

.crm-tabs-panel::v-deep .nav-tabs {
  border-bottom: 1px solid #e4e9f1;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding: 0 18px;
}

.crm-tabs-panel::v-deep .nav-link {
  border: 0;
  color: #647084;
  font-weight: 800;
  padding: 16px 12px 13px;
  white-space: nowrap;
}

.crm-tabs-panel::v-deep .nav-link.active {
  border-bottom: 3px solid #0bb7d4;
  color: #0b87c9;
}

.crm-tabs-panel::v-deep .tab-content {
  padding: 18px;
}

.crm-tab-toolbar {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 14px;
}

.crm-tab-toolbar > * {
  min-width: 0;
}

.crm-tab-toolbar strong {
  color: #25324b;
}

.crm-timeline-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.crm-timeline-filter {
  align-items: center;
  background: #f7f9fc;
  border: 1px solid #dfe5ee;
  border-radius: 8px;
  color: #52637a;
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  min-height: 34px;
  padding: 7px 10px;
  white-space: normal;
}

.crm-timeline-filter.active {
  background: #ecfbff;
  border-color: #0bb7d4;
  color: #0b87c9;
}

.crm-quick-note {
  align-items: flex-start;
  background: #f7f9fc;
  border: 1px solid #e4e9f1;
  border-radius: 8px;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) auto;
  margin-bottom: 16px;
  padding: 12px;
}

.crm-timeline-group {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}

.crm-timeline-group-title {
  align-items: center;
  color: #647084;
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.crm-timeline-group-title span {
  color: #25324b;
  font-size: 13px;
  font-weight: 800;
}

.crm-timeline-group-title small {
  color: #7b8499;
  font-size: 12px;
}

.crm-task-list {
  display: grid;
  gap: 10px;
}

.crm-technical-request-list {
  display: grid;
  gap: 10px;
  margin-bottom: 12px;
}

.crm-technical-request {
  align-items: flex-start;
  background: #f6fbff;
  border: 1px solid #cce8ff;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-width: 0;
  padding: 13px 14px;
}

.crm-technical-request-info {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.crm-technical-request-info strong {
  color: #25324b;
  font-size: 14px;
  font-weight: 800;
}

.crm-technical-request-info span,
.crm-technical-request-info small {
  color: #647084;
  font-size: 12px;
}

.crm-technical-request-actions {
  display: flex;
  flex: 0 0 380px;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

.crm-task-item {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e4e9f1;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-width: 0;
  padding: 13px 14px;
}

.crm-task-item > div:first-child {
  min-width: 0;
}

.crm-task-item.late {
  background: #fff8fa;
  border-color: #ffd6de;
}

.crm-task-item strong {
  color: #25324b;
  display: block;
  margin-bottom: 4px;
  overflow-wrap: anywhere;
}

.crm-task-item span {
  color: #647084;
  font-size: 12px;
}

.crm-task-item p {
  color: #647084;
  margin: 8px 0 0;
  overflow-wrap: anywhere;
  white-space: pre-line;
}

.crm-task-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.crm-task-actions .btn {
  white-space: normal;
}

.crm-note-box {
  background: #f7f9fc;
  border: 1px solid #e4e9f1;
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 14px;
}

.crm-contact-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  margin-bottom: 14px;
}

.crm-timeline-item {
  display: grid;
  gap: 12px;
  grid-template-columns: 12px 1fr;
  padding: 0 0 18px;
}

.crm-timeline-dot {
  background: #3699ff;
  border-radius: 50%;
  height: 10px;
  margin-top: 6px;
  width: 10px;
}

.crm-timeline-descricao {
  overflow-wrap: anywhere;
  white-space: pre-line;
}

.crm-agenda-horarios {
  background: #f8f9fb;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 14px;
}

.crm-agenda-context {
  color: #7e8299;
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-top: 4px;
}

.crm-agenda-cliente {
  background: #f8f9fb;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 14px;
}

.crm-agenda-cliente span {
  color: #7e8299;
  font-size: 12px;
}

.crm-agenda-cliente strong {
  color: #3f4254;
}

.crm-agenda-slots {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fill, minmax(116px, 1fr));
}

.crm-agenda-slot {
  background: #ffffff;
  border: 1px solid #1bc5bd;
  border-radius: 8px;
  color: #3f4254;
  display: flex;
  flex-direction: column;
  min-height: 54px;
  padding: 8px 10px;
  text-align: left;
}

.crm-agenda-slot strong,
.crm-agenda-slot small {
  overflow-wrap: anywhere;
}

.crm-agenda-slot.selected {
  background: #e8fff3;
  border-color: #1bc5bd;
  box-shadow: 0 0 0 2px rgba(27, 197, 189, 0.2);
  color: #181c32;
}

.crm-agenda-slot.selected::after {
  color: #1bc5bd;
  content: "Selecionado";
  font-size: 11px;
  font-weight: 700;
  margin-top: 4px;
}

.crm-agenda-slot small {
  color: #7e8299;
}

.crm-agenda-slot.unavailable {
  background: #f3f6f9;
  border-color: #e4e6ef;
  color: #7e8299;
}

.crm-drawer-body {
  padding: 18px;
}

.crm-drawer-footer {
  align-items: center;
  background: #ffffff;
  border-top: 1px solid #e4e9f1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  padding: 12px 16px;
}

.crm-drawer-footer .btn {
  white-space: normal;
}

.crm-benefit-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.crm-benefit-chip {
  background: #eaf6ff;
  border: 1px solid #cfe9ff;
  border-radius: 6px;
  color: #1f5f93;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  padding: 8px 10px;
}

.crm-benefit-chip:hover,
.crm-benefit-chip:focus {
  background: #dff2ff;
  border-color: #9fd5ff;
  outline: none;
}

.crm-benefit-chip.active {
  background: #0bb7d4;
  border-color: #0aa4bf;
  color: #ffffff;
}

@media (max-width: 991px) {
  .crm-opportunity-hero {
    display: block;
  }

  .crm-hero-actions {
    justify-content: flex-start;
    margin-top: 14px;
    max-width: 100%;
    min-width: 0;
  }

  .crm-opportunity-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .crm-opportunity-side {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .crm-opportunity-main {
    min-width: 0;
  }
}

@media (max-width: 767px) {
  .crm-page-header {
    display: block;
  }

  .crm-header-actions {
    justify-content: flex-start;
    margin-top: 12px;
  }

  .crm-detail-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  .crm-detail-row strong {
    max-width: 100%;
    text-align: left;
  }

  .crm-opportunity-hero,
  .crm-opportunity-layout {
    display: block;
  }

  .crm-hero-actions,
  .crm-opportunity-main {
    justify-content: flex-start;
    margin-top: 12px;
  }

  .crm-action-group {
    width: 100%;
  }

  .crm-opportunity-side {
    margin-bottom: 16px;
  }

  .crm-task-actions,
  .crm-task-item,
  .crm-technical-request,
  .crm-technical-request-actions,
  .crm-quick-note,
  .crm-tab-toolbar {
    align-items: flex-start;
    flex-direction: column;
    grid-template-columns: minmax(0, 1fr);
  }

  .crm-technical-request-actions {
    flex-basis: auto;
    width: 100%;
  }

  .crm-opportunity-side {
    display: flex;
  }

  .crm-stage-bar {
    display: flex;
  }

  .crm-stage-item {
    flex: 0 0 210px;
  }

  .crm-hero-actions .btn,
  .crm-action-button,
  .crm-drawer-footer .btn,
  .crm-task-actions .btn,
  .crm-technical-request-actions .btn,
  .crm-tab-toolbar .btn {
    flex: 1 1 160px;
  }
}

@media (max-width: 575px) {
  .crm-opportunity-hero,
  .crm-panel {
    padding: 16px;
  }

  .crm-hero-actions .btn,
  .crm-action-button,
  .crm-drawer-footer .btn,
  .crm-task-actions .btn,
  .crm-tab-toolbar .btn {
    flex-basis: 100%;
  }

  .crm-tabs-panel::v-deep .tab-content {
    padding: 14px;
  }
}
</style>
